import React, { useCallback, useEffect, useMemo, useState } from "react";

import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    Dialog,
    DialogContent,
    DialogTitle,
    Divider,
    IconButton,
    InputAdornment,
    LinearProgress,
    Paper,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    TextField,
    Tooltip,
    Typography,
} from "@mui/material";

import {
    AccessTimeRounded,
    AssessmentRounded,
    CalendarTodayRounded,
    ChevronLeftRounded,
    ChevronRightRounded,
    GroupsRounded,
    RefreshRounded,
    SearchRounded,
    TrendingUpRounded,
    WarningAmberRounded,
} from "@mui/icons-material";

import axios from "../../../axiosMiddleware/index";

import { api } from "../../../apiendpoints/index";

const PAGE_SIZE = 5;
const SECTION_PAGE_SIZE = 5;

const LIST_TABLE_CONTAINER_SX = {
    maxHeight: 380,
    border: "1px solid",
    borderColor: "divider",
    borderRadius: 2,
    overflow: "auto",
};

const LATE_ARRIVAL_EMPLOYEE_DETAILS_ENDPOINT =
    api?.TIME_ATTENDANCE?.LATE_ARRIVAL_EMPLOYEE_DETAILS ||
    "/TimeAndAttendance/GetLateArrivalEmployeeDetails";

const getLocalDateInputValue = (date) => {
    const year = date.getFullYear();

    const month = String(date.getMonth() + 1).padStart(2, "0");

    const day = String(date.getDate()).padStart(2, "0");

    return `${year}-${month}-${day}`;
};

const getTodayInputValue = () => {
    return getLocalDateInputValue(new Date());
};

const getCurrentMonthInputValue = () => {
    const date = new Date();
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;
};

const getFtoMonthLabel = (monthValue) => {
    const raw = String(monthValue || "");
    const match = raw.match(/^(\d{4})-(\d{2})$/);

    if (!match) {
        return "Current Month";
    }

    const year = Number(match[1]);
    const month = Number(match[2]);

    if (!year || month < 1 || month > 12) {
        return "Current Month";
    }

    return new Date(year, month - 1, 1).toLocaleDateString("en-IN", {
        month: "long",
        year: "numeric",
    });
};

const getYesterdayInputValue = () => {
    const date = new Date();

    date.setDate(date.getDate() - 1);

    return getLocalDateInputValue(date);
};

const addDays = (dateString, days) => {
    const date = new Date(`${dateString}T00:00:00`);

    date.setDate(date.getDate() + days);

    return getLocalDateInputValue(date);
};

const formatSelectedDate = (dateString) => {
    if (!dateString) {
        return "-";
    }

    const date = new Date(`${dateString}T00:00:00`);

    return date.toLocaleDateString("en-IN", {
        weekday: "short",

        day: "2-digit",

        month: "short",

        year: "numeric",
    });
};

const formatMinutes = (value) => {
    const number = Number(value || 0);

    if (!Number.isFinite(number)) {
        return "0 min";
    }

    return `${Math.round(number)} min`;
};

const getStatusMeta = (lateMinutes) => {
    const minutes = Number(lateMinutes || 0);

    if (minutes >= 30) {
        return {
            label: "Severe",

            color: "error",
        };
    }

    if (minutes >= 15) {
        return {
            label: "Late",

            color: "warning",
        };
    }

    return {
        label: "Slightly Late",

        color: "info",
    };
};

const getPercentage = (value, total) => {
    if (!total || total <= 0) {
        return 0;
    }

    return Math.round((value / total) * 100);
};

const getNumericValue = (value) => {
    const number = Number(value);

    return Number.isFinite(number) ? number : 0;
};

const TrendChart = ({
    data = [],
    series = [],
    height = 190,
    emptyText = "No trend data available.",
}) => {
    const normalized = (data || [])
        .map((item) => ({
            ...item,
            chartDate: item?.date ?? item?.Date ?? item?.attendanceDate ?? item?.AttendanceDate,
        }))
        .filter((item) => item.chartDate);

    if (!normalized.length || !series.length) {
        return (
            <Box
                sx={{
                    height,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                }}
            >
                <Typography variant="body2" color="text.secondary">
                    {emptyText}
                </Typography>
            </Box>
        );
    }

    const values = normalized.flatMap((item) =>
        series.map((itemSeries) => getNumericValue(item?.[itemSeries.key])),
    );

    const maxValue = Math.max(...values, 1);

    const width = 640;
    const chartHeight = 150;
    const paddingX = 28;
    const paddingY = 16;

    const getX = (index) =>
        normalized.length <= 1
            ? width / 2
            : paddingX + (index / (normalized.length - 1)) * (width - paddingX * 2);

    const getY = (value) =>
        chartHeight - paddingY - (value / maxValue) * (chartHeight - paddingY * 2);

    return (
        <Box sx={{ width: "100%" }}>
            <Box
                sx={{
                    width: "100%",
                    overflow: "hidden",
                    borderRadius: 2,
                }}
            >
                <svg
                    viewBox={`0 0 ${width} ${chartHeight}`}
                    width="100%"
                    height={height}
                    role="img"
                    aria-label="Trend chart"
                >
                    {[0, 25, 50, 75, 100].map((percent) => {
                        const y =
                            chartHeight -
                            paddingY -
                            (percent / 100) * (chartHeight - paddingY * 2);

                        return (
                            <line
                                key={percent}
                                x1={paddingX}
                                x2={width - paddingX}
                                y1={y}
                                y2={y}
                                stroke="#e9edf2"
                                strokeWidth="1"
                            />
                        );
                    })}

                    {series.map((itemSeries) => {
                        const points = normalized
                            .map((item, index) => {
                                const value = getNumericValue(item?.[itemSeries.key]);

                                return `${getX(index)},${getY(value)}`;
                            })
                            .join(" ");

                        return (
                            <g key={itemSeries.key}>
                                <polyline
                                    fill="none"
                                    stroke={itemSeries.color}
                                    strokeWidth="3"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    points={points}
                                />

                                {normalized.map((item, index) => {
                                    const value = getNumericValue(item?.[itemSeries.key]);

                                    return (
                                        <circle
                                            key={`${itemSeries.key}-${index}`}
                                            cx={getX(index)}
                                            cy={getY(value)}
                                            r="3.5"
                                            fill="#ffffff"
                                            stroke={itemSeries.color}
                                            strokeWidth="2"
                                        >
                                            <title>
                                                {`${item.chartDate}: ${value}${itemSeries.suffix || ""
                                                    }`}
                                            </title>
                                        </circle>
                                    );
                                })}
                            </g>
                        );
                    })}
                </svg>
            </Box>

            <Stack
                direction="row"
                spacing={2}
                flexWrap="wrap"
                useFlexGap
                sx={{ px: 0.5, mt: 0.25 }}
            >
                {series.map((itemSeries) => (
                    <Stack
                        key={itemSeries.key}
                        direction="row"
                        spacing={0.65}
                        alignItems="center"
                    >
                        <Box
                            sx={{
                                width: 9,
                                height: 9,
                                borderRadius: "50%",
                                backgroundColor: itemSeries.color,
                            }}
                        />
                        <Typography variant="caption" color="text.secondary">
                            {itemSeries.label}
                        </Typography>
                    </Stack>
                ))}
            </Stack>

        </Box>
    );
};

const getRawDateOnly = (value) => {
    const raw = String(value ?? "-");
    if (!raw || raw === "-") {
        return "-";
    }

    return raw.split(" ")[0].split("T")[0];
};

const getFtoLeaveTypeName = (record) => {
    const backendName = String(record?.leaveTypeName ?? "").trim();
    if (backendName) {
        return backendName;
    }

    switch (Number(record?.leaveTypeId ?? 0)) {
        case 1:
            return "EL";
        case 2:
            return "CL";
        case 5:
            return "SL";
        default:
            return "Not Specified";
    }
};

const getFtoTypeName = (record) => {
    const backendName = String(record?.ftoTypeName ?? "").trim();
    if (backendName) {
        return backendName;
    }

    switch (Number(record?.ftoTypeId ?? 0)) {
        case 1:
            return "Flexible Leave off";
        case 2:
            return "Flexible Leave In";
        default:
            return "Unknown FTO Type";
    }
};

const getDateMonthKey = (value) => {
    const raw = String(value ?? "");

    const isoMatch = raw.match(/^(\d{4})-(\d{2})-/);
    if (isoMatch) {
        return `${isoMatch[1]}-${isoMatch[2]}`;
    }

    const dmyMatch = raw.match(/^(\d{2})-(\d{2})-(\d{4})/);
    if (dmyMatch) {
        return `${dmyMatch[3]}-${dmyMatch[2]}`;
    }

    return "";
};

const MonthlyLateEmployeeList = ({
    data = [],
    searchKeyword = "",
    onViewDetails,
    paginationResetKey,
}) => {
    const items = (data || [])
        .map((item) => ({
            ...item,
            personId: Number(item?.personId || 0),
            employeeName: item?.employeeName || "Unknown employee",
            organization: item?.organization || "",
            lateArrivalCount: getNumericValue(item?.lateArrivalCount),
            averageLateMinutes: getNumericValue(item?.averageLateMinutes),
            maximumLateMinutes: getNumericValue(item?.maximumLateMinutes),
        }))
        .sort((a, b) => {
            if (b.lateArrivalCount !== a.lateArrivalCount) {
                return b.lateArrivalCount - a.lateArrivalCount;
            }

            if (b.averageLateMinutes !== a.averageLateMinutes) {
                return b.averageLateMinutes - a.averageLateMinutes;
            }

            return a.employeeName.localeCompare(b.employeeName);
        });

    const filtered = items.filter((item) => {
        const query = String(searchKeyword || "").trim().toLowerCase();

        if (!query) {
            return true;
        }

        return (
            item.employeeName.toLowerCase().includes(query) ||
            String(item.personId).includes(query) ||
            item.organization.toLowerCase().includes(query)
        );
    });

    const [tablePage, setTablePage] = useState(0);

    useEffect(() => {
        setTablePage(0);
    }, [searchKeyword, paginationResetKey]);

    useEffect(() => {
        const maxPage = Math.max(
            0,
            Math.ceil(filtered.length / SECTION_PAGE_SIZE) - 1,
        );

        if (tablePage > maxPage) {
            setTablePage(maxPage);
        }
    }, [filtered.length, tablePage]);

    const paginated = filtered.slice(
        tablePage * SECTION_PAGE_SIZE,
        tablePage * SECTION_PAGE_SIZE + SECTION_PAGE_SIZE,
    );

    const maxCount = Math.max(
        ...items.map((item) => item.lateArrivalCount),
        1,
    );

    if (!filtered.length) {
        return (
            <>
                <Box
                    sx={{
                        minHeight: 180,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        border: "1px dashed",
                        borderColor: "divider",
                        borderRadius: 2,
                    }}
                >
                    <Typography variant="body2" color="text.secondary">
                        {items.length
                            ? "No monthly employee matches your search."
                            : "No late employees found for the selected month."}
                    </Typography>
                </Box>
            </>
        );
    }

    return (
        <>
            <TableContainer sx={LIST_TABLE_CONTAINER_SX}>
                <Table size="small" stickyHeader>
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{ fontWeight: 850, width: 55 }}>#</TableCell>
                            <TableCell sx={{ fontWeight: 850, minWidth: 260 }}>
                                Employee
                            </TableCell>
                            <TableCell
                                align="center"
                                sx={{ fontWeight: 850, minWidth: 100 }}
                            >
                                Late times
                            </TableCell>
                            <TableCell
                                align="center"
                                sx={{ fontWeight: 850, minWidth: 110 }}
                            >
                                Avg delay
                            </TableCell>
                            <TableCell
                                align="center"
                                sx={{ fontWeight: 850, minWidth: 110 }}
                            >
                                Max delay
                            </TableCell>
                            <TableCell
                                align="right"
                                sx={{ fontWeight: 850, minWidth: 120 }}
                            >
                                Action
                            </TableCell>
                        </TableRow>
                    </TableHead>

                    <TableBody>
                        {paginated.map((item, index) => {
                            const absoluteIndex =
                                tablePage * SECTION_PAGE_SIZE + index;

                            const percentage =
                                maxCount > 0
                                    ? Math.max(
                                        4,
                                        Math.min(
                                            100,
                                            (item.lateArrivalCount / maxCount) * 100,
                                        ),
                                    )
                                    : 0;

                            const isMoreThanTwoMinutesLate =
                                item.maximumLateMinutes > 2;

                            return (
                                <TableRow
                                    key={`${item.personId}-${item.employeeName}-${absoluteIndex}`}
                                    hover
                                    sx={
                                        isMoreThanTwoMinutesLate
                                            ? {
                                                backgroundColor:
                                                    "rgba(211, 47, 47, 0.035)",
                                                "&:hover": {
                                                    backgroundColor:
                                                        "rgba(211, 47, 47, 0.07)",
                                                },
                                            }
                                            : {}
                                    }
                                >
                                    <TableCell>
                                        <Box
                                            sx={{
                                                width: 28,
                                                height: 28,
                                                borderRadius: "50%",
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                backgroundColor:
                                                    absoluteIndex === 0
                                                        ? "#fff1ea"
                                                        : "action.hover",
                                                color:
                                                    absoluteIndex === 0
                                                        ? "#f36f43"
                                                        : "text.secondary",
                                                fontWeight: 850,
                                                fontSize: 12,
                                            }}
                                        >
                                            {absoluteIndex + 1}
                                        </Box>
                                    </TableCell>

                                    <TableCell>
                                        <Box sx={{ minWidth: 0 }}>
                                            <Typography
                                                variant="body2"
                                                sx={{
                                                    fontWeight: 850,
                                                    overflow: "hidden",
                                                    textOverflow: "ellipsis",
                                                    whiteSpace: "nowrap",
                                                }}
                                            >
                                                {item.employeeName}
                                            </Typography>

                                            <Typography
                                                variant="caption"
                                                color="text.secondary"
                                                sx={{
                                                    display: "block",
                                                    mt: 0.2,
                                                    overflow: "hidden",
                                                    textOverflow: "ellipsis",
                                                    whiteSpace: "nowrap",
                                                }}
                                            >
                                                ID: {item.personId || "-"}
                                                {item.organization
                                                    ? ` • ${item.organization}`
                                                    : ""}
                                            </Typography>

                                            <LinearProgress
                                                variant="determinate"
                                                value={percentage}
                                                sx={{
                                                    mt: 0.7,
                                                    height: 5,
                                                    borderRadius: 99,
                                                    backgroundColor: "action.hover",
                                                    "& .MuiLinearProgress-bar": {
                                                        borderRadius: 99,
                                                        backgroundColor:
                                                            isMoreThanTwoMinutesLate
                                                                ? "#ef5350"
                                                                : "#42a5f5",
                                                    },
                                                }}
                                            />
                                        </Box>
                                    </TableCell>

                                    <TableCell align="center">
                                        <Typography
                                            variant="body2"
                                            sx={{
                                                fontWeight: 900,
                                                fontSize: 18,
                                            }}
                                        >
                                            {item.lateArrivalCount}
                                        </Typography>
                                    </TableCell>

                                    <TableCell align="center">
                                        <Typography
                                            variant="body2"
                                            sx={{
                                                fontWeight: 800,
                                                color: isMoreThanTwoMinutesLate
                                                    ? "error.main"
                                                    : "text.primary",
                                            }}
                                        >
                                            {item.averageLateMinutes.toFixed(1)} min
                                        </Typography>
                                    </TableCell>

                                    <TableCell align="center">
                                        <Typography
                                            variant="body2"
                                            sx={{
                                                fontWeight: 800,
                                                color: isMoreThanTwoMinutesLate
                                                    ? "error.main"
                                                    : "text.primary",
                                            }}
                                        >
                                            {item.maximumLateMinutes.toFixed(1)} min
                                        </Typography>
                                    </TableCell>

                                    <TableCell align="right">
                                        <Button
                                            size="small"
                                            variant="outlined"
                                            onClick={() => onViewDetails(item)}
                                            sx={{
                                                textTransform: "none",
                                                borderRadius: 1.75,
                                                fontWeight: 800,
                                            }}
                                        >
                                            View Details
                                        </Button>
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>

            <Divider sx={{ mt: 1 }} />

            <TablePagination
                component="div"
                count={filtered.length}
                page={tablePage}
                onPageChange={(_, newPage) => setTablePage(newPage)}
                rowsPerPage={SECTION_PAGE_SIZE}
                rowsPerPageOptions={[SECTION_PAGE_SIZE]}
                labelRowsPerPage="Rows:"
            />
        </>
    );
};

const SeverityDonut = ({
    under15 = 0,
    moderate = 0,
    severe = 0,
    centerLabel = "Late",
    centerValue,
}) => {
    const values = [
        {
            label: "<15 min",
            value: getNumericValue(under15),
            color: "#42a5f5",
        },
        {
            label: "15–29 min",
            value: getNumericValue(moderate),
            color: "#ffb300",
        },
        {
            label: "30+ min",
            value: getNumericValue(severe),
            color: "#ef5350",
        },
    ];

    const total = values.reduce((sum, item) => sum + item.value, 0);

    const radius = 56;
    const circumference = 2 * Math.PI * radius;
    let offset = 0;

    return (
        <Stack
            direction={{ xs: "column", sm: "row" }}
            alignItems="center"
            spacing={1.5}
            sx={{ minHeight: 170 }}
        >
            <Box
                sx={{
                    position: "relative",
                    width: 145,
                    height: 145,
                    flexShrink: 0,
                }}
            >
                <svg
                    viewBox="0 0 145 145"
                    width="145"
                    height="145"
                    role="img"
                    aria-label="Severity distribution"
                >
                    <circle
                        cx="72.5"
                        cy="72.5"
                        r={radius}
                        fill="none"
                        stroke="#edf0f4"
                        strokeWidth="15"
                    />

                    {values.map((item) => {
                        const fraction = total > 0 ? item.value / total : 0;
                        const dash = fraction * circumference;
                        const currentOffset = offset;
                        offset += dash;

                        if (dash <= 0) {
                            return null;
                        }

                        return (
                            <circle
                                key={item.label}
                                cx="72.5"
                                cy="72.5"
                                r={radius}
                                fill="none"
                                stroke={item.color}
                                strokeWidth="15"
                                strokeDasharray={`${dash} ${circumference - dash}`}
                                strokeDashoffset={-currentOffset}
                                strokeLinecap="round"
                                transform="rotate(-90 72.5 72.5)"
                            />
                        );
                    })}
                </svg>

                <Box
                    sx={{
                        position: "absolute",
                        inset: 0,
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                    }}
                >
                    <Typography
                        sx={{
                            fontSize: 22,
                            lineHeight: 1,
                            fontWeight: 900,
                        }}
                    >
                        {centerValue ?? total}
                    </Typography>
                    <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ mt: 0.35 }}
                    >
                        {centerLabel}
                    </Typography>
                </Box>
            </Box>

            <Stack spacing={0.95} sx={{ width: "100%" }}>
                {values.map((item) => (
                    <Stack
                        key={item.label}
                        direction="row"
                        alignItems="center"
                        justifyContent="space-between"
                        spacing={1}
                    >
                        <Stack direction="row" spacing={0.75} alignItems="center">
                            <Box
                                sx={{
                                    width: 8,
                                    height: 8,
                                    borderRadius: "50%",
                                    backgroundColor: item.color,
                                }}
                            />
                            <Typography variant="caption" color="text.secondary">
                                {item.label}
                            </Typography>
                        </Stack>

                        <Typography variant="body2" sx={{ fontWeight: 850 }}>
                            {item.value}
                            {total > 0
                                ? ` (${Math.round((item.value / total) * 100)}%)`
                                : " (0%)"}
                        </Typography>
                    </Stack>
                ))}
            </Stack>
        </Stack>
    );
};

const PeriodComparisonChart = ({ periods = [] }) => {
    const items = periods
        .map((item) => ({
            label: item.label,
            value: getNumericValue(item.value),
        }))
        .filter((item) => item.value >= 0);

    if (!items.length) {
        return (
            <Typography variant="body2" color="text.secondary">
                No period data available.
            </Typography>
        );
    }

    const maxValue = Math.max(...items.map((item) => item.value), 1);

    return (
        <Stack spacing={1}>
            {items.map((item) => (
                <Box key={item.label}>
                    <Stack
                        direction="row"
                        justifyContent="space-between"
                        spacing={1}
                        sx={{ mb: 0.35 }}
                    >
                        <Typography variant="caption" sx={{ fontWeight: 750 }}>
                            {item.label}
                        </Typography>
                        <Typography variant="caption" sx={{ fontWeight: 850 }}>
                            {item.value.toFixed(2)} min
                        </Typography>
                    </Stack>

                    <LinearProgress
                        variant="determinate"
                        value={(item.value / maxValue) * 100}
                        sx={{
                            height: 9,
                            borderRadius: 99,
                            backgroundColor: "action.hover",
                            "& .MuiLinearProgress-bar": {
                                borderRadius: 99,
                                backgroundColor: "#f36f43",
                            },
                        }}
                    />
                </Box>
            ))}
        </Stack>
    );
};

const StatCard = ({
    title,

    value,

    subtitle,

    icon,

    accent,
}) => {
    return (
        <Card
            elevation={0}
            sx={{
                height: "100%",

                border: "1px solid",

                borderColor: "divider",

                borderRadius: 3,

                backgroundColor: "background.paper",

                transition: "all 180ms ease",

                "&:hover": {
                    boxShadow: "0 10px 28px rgba(0,0,0,0.06)",

                    transform: "translateY(-1px)",
                },
            }}
        >
            <CardContent
                sx={{
                    p: 2.25,

                    "&:last-child": {
                        pb: 2.25,
                    },
                }}
            >
                <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="flex-start"
                    spacing={2}
                >
                    <Box sx={{ minWidth: 0 }}>
                        <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{
                                fontWeight: 700,

                                mb: 0.8,
                            }}
                        >
                            {title}
                        </Typography>

                        <Typography
                            sx={{
                                fontSize: 28,

                                lineHeight: 1.1,

                                fontWeight: 800,

                                letterSpacing: "-0.02em",
                            }}
                        >
                            {value}
                        </Typography>

                        <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{
                                display: "block",

                                mt: 0.8,

                                whiteSpace: "nowrap",

                                overflow: "hidden",

                                textOverflow: "ellipsis",
                            }}
                            title={subtitle}
                        >
                            {subtitle}
                        </Typography>
                    </Box>

                    <Box
                        sx={{
                            width: 44,

                            height: 44,

                            minWidth: 44,

                            borderRadius: 2,

                            display: "flex",

                            alignItems: "center",

                            justifyContent: "center",

                            color: accent,

                            backgroundColor: `${accent}14`,
                        }}
                    >
                        {icon}
                    </Box>
                </Stack>
            </CardContent>
        </Card>
    );
};

const SeverityRow = ({
    label,

    count,

    total,

    progressColor,

    description,
}) => {
    const percentage = getPercentage(count, total);

    return (
        <Box sx={{ mb: 2.1 }}>
            <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                sx={{ mb: 0.6 }}
            >
                <Box>
                    <Typography variant="body2" sx={{ fontWeight: 700 }}>
                        {label}
                    </Typography>

                    <Typography variant="caption" color="text.secondary">
                        {description}
                    </Typography>
                </Box>

                <Stack direction="row" alignItems="center" spacing={1}>
                    <Typography variant="body2" sx={{ fontWeight: 800 }}>
                        {count}
                    </Typography>

                    <Typography variant="caption" color="text.secondary">
                        {percentage}%
                    </Typography>
                </Stack>
            </Stack>

            <LinearProgress
                variant="determinate"
                value={percentage}
                sx={{
                    height: 8,

                    borderRadius: 20,

                    backgroundColor: "action.hover",

                    "& .MuiLinearProgress-bar": {
                        borderRadius: 20,

                        backgroundColor: progressColor,
                    },
                }}
            />
        </Box>
    );
};

const TopLateEmployee = ({ row, rank, maxLate, onClick }) => {
    const lateMinutes = Number(row?.lateMinutes || 0);

    const percentage =
        maxLate > 0
            ? Math.max(
                4,

                Math.min(
                    100,

                    (lateMinutes / maxLate) * 100,
                ),
            )
            : 0;

    const statusMeta = getStatusMeta(lateMinutes);
    const isMoreThanTwoMinutesLate = lateMinutes > 2;

    return (
        <Box
            onClick={onClick}
            sx={{
                py: 1.2,
                px: 0.75,
                borderRadius: 2,
                cursor: "pointer",
                transition: "background-color 160ms ease",
                "&:hover": {
                    backgroundColor: "action.hover",
                },
                "&:last-child": {
                    pb: 0,
                },
            }}
        >
            <Stack direction="row" spacing={1.4} alignItems="center">
                <Box
                    sx={{
                        width: 28,

                        height: 28,

                        borderRadius: "50%",

                        display: "flex",

                        alignItems: "center",

                        justifyContent: "center",

                        flexShrink: 0,

                        backgroundColor: rank === 1 ? "#fff1ea" : "action.hover",

                        color: rank === 1 ? "#f36f43" : "text.secondary",

                        fontWeight: 800,

                        fontSize: 12,
                    }}
                >
                    {rank}
                </Box>

                <Box
                    sx={{
                        minWidth: 0,

                        flex: 1,
                    }}
                >
                    <Stack
                        direction="row"
                        alignItems="center"
                        justifyContent="space-between"
                        spacing={1}
                    >
                        <Box sx={{ minWidth: 0 }}>
                            <Typography
                                variant="body2"
                                sx={{
                                    fontWeight: 800,

                                    overflow: "hidden",

                                    textOverflow: "ellipsis",

                                    whiteSpace: "nowrap",
                                }}
                            >
                                {row?.employeeName || "-"}
                            </Typography>

                            <Typography variant="caption" color="text.secondary">
                                ID: {row?.personId || "-"} • In Time {row?.checkIn || "-"}
                            </Typography>
                        </Box>

                        <Stack
                            direction="row"
                            alignItems="center"
                            spacing={1}
                            sx={{ flexShrink: 0 }}
                        >
                            <Typography
                                variant="body2"
                                sx={{
                                    fontWeight: 800,
                                    color: isMoreThanTwoMinutesLate
                                        ? "error.main"
                                        : "text.primary",
                                }}
                            >
                                {formatMinutes(lateMinutes)}
                            </Typography>

                            <Chip
                                label={statusMeta.label}
                                color={isMoreThanTwoMinutesLate ? "error" : statusMeta.color}
                                size="small"
                                sx={{
                                    fontWeight: 700,

                                    display: {
                                        xs: "none",

                                        sm: "inline-flex",
                                    },
                                }}
                            />
                        </Stack>
                    </Stack>

                    <LinearProgress
                        variant="determinate"
                        value={percentage}
                        sx={{
                            mt: 0.8,

                            height: 5,

                            borderRadius: 99,

                            backgroundColor: "action.hover",

                            "& .MuiLinearProgress-bar": {
                                borderRadius: 99,

                                backgroundColor:
                                    isMoreThanTwoMinutesLate
                                        ? "#ef5350"
                                        : "#42a5f5",
                            },
                        }}
                    />
                </Box>
            </Stack>
        </Box>
    );
};

const formatNullableNumber = (value, suffix = "") => {
    const number = Number(value);

    if (!Number.isFinite(number)) {
        return "-";
    }

    return `${number.toFixed(2)}${suffix}`;
};

const DetailPeriodCard = ({ title, period }) => {
    const stats = period || {};

    return (
        <Card
            elevation={0}
            sx={{
                border: "1px solid",

                borderColor: "divider",

                borderRadius: 2.5,

                height: "100%",
            }}
        >
            <CardContent sx={{ p: 1.75 }}>
                <Typography variant="body2" sx={{ fontWeight: 800, mb: 1.2 }}>
                    {title}
                </Typography>

                <Stack spacing={0.8}>
                    <Stack direction="row" justifyContent="space-between">
                        <Typography variant="caption" color="text.secondary">
                            Late days
                        </Typography>

                        <Typography variant="body2" sx={{ fontWeight: 800 }}>
                            {Number(stats.lateDayCount || 0)}
                        </Typography>
                    </Stack>

                    <Stack direction="row" justifyContent="space-between">
                        <Typography variant="caption" color="text.secondary">
                            Late records
                        </Typography>

                        <Typography variant="body2" sx={{ fontWeight: 800 }}>
                            {Number(stats.lateRecordCount || 0)}
                        </Typography>
                    </Stack>

                    <Stack direction="row" justifyContent="space-between">
                        <Typography variant="caption" color="text.secondary">
                            Average delay
                        </Typography>

                        <Typography variant="body2" sx={{ fontWeight: 800 }}>
                            {formatNullableNumber(
                                stats.averageLateMinutes,

                                " min",
                            )}
                        </Typography>
                    </Stack>

                    <Stack direction="row" justifyContent="space-between">
                        <Typography variant="caption" color="text.secondary">
                            Maximum delay
                        </Typography>

                        <Typography variant="body2" sx={{ fontWeight: 800 }}>
                            {formatNullableNumber(
                                stats.maximumLateMinutes,

                                " min",
                            )}
                        </Typography>
                    </Stack>

                    <Divider sx={{ my: 0.5 }} />

                    <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                        <Chip
                            size="small"
                            label={`<15: ${Number(stats.under15MinutesCount || 0)}`}
                        />

                        <Chip
                            size="small"
                            color="warning"
                            label={`15–29: ${Number(stats.moderateLateCount || 0)}`}
                        />

                        <Chip
                            size="small"
                            color="error"
                            label={`30+: ${Number(stats.severeLateCount || 0)}`}
                        />
                    </Stack>
                </Stack>
            </CardContent>
        </Card>
    );
};

export default function LateArrivalDashboard() {
    const [selectedDate, setSelectedDate] = useState(getTodayInputValue());

    const [searchKeyword, setSearchKeyword] = useState("");

    const [page, setPage] = useState(0);

    const [rows, setRows] = useState([]);

    const [topLateArrivals, setTopLateArrivals] = useState([]);

    const [totalRecords, setTotalRecords] = useState(0);

    const [summary, setSummary] = useState({
        totalLateArrivals: 0,

        averageLateMinutes: 0,

        maxLateMinutes: 0,

        severeLateCount: 0,

        moderateLateCount: 0,

        under15LateCount: 0,
    });

    const [loading, setLoading] = useState(false);

    const [error, setError] = useState("");

    const [lastLoadedDate, setLastLoadedDate] = useState("");

    const [detailsOpen, setDetailsOpen] = useState(false);

    const [detailsLoading, setDetailsLoading] = useState(false);

    const [detailsError, setDetailsError] = useState("");

    const [selectedEmployee, setSelectedEmployee] = useState(null);

    const [employeeDetails, setEmployeeDetails] = useState(null);

    const [trend, setTrend] = useState([]);


    const [monthlyEmployeeLateFrequency, setMonthlyEmployeeLateFrequency] =
        useState([]);
    const [monthlyEmployeeSearch, setMonthlyEmployeeSearch] = useState("");
    const [monthlyLateMonth, setMonthlyLateMonth] = useState(getCurrentMonthInputValue());

    const [ftoOverview, setFtoOverview] = useState({
        totalFtoCount: 0,
        totalFtoDays: 0,
        employeesWithFto: 0,
        approvedFtoCount: 0,
        pendingFtoCount: 0,
        employees: [],
        records: [],
    });

    const [ftoSearch, setFtoSearch] = useState("");
    const [ftoPage, setFtoPage] = useState(0);
    const [ftoMonth, setFtoMonth] = useState(getCurrentMonthInputValue());
    const [ftoDetailsOpen, setFtoDetailsOpen] = useState(false);
    const [selectedFtoEmployee, setSelectedFtoEmployee] = useState(null);
    const [absentWithoutFtoEmployees, setAbsentWithoutFtoEmployees] = useState([]);
    const [absentWithoutFtoSearch, setAbsentWithoutFtoSearch] = useState("");
    const [absentWithoutFtoPage, setAbsentWithoutFtoPage] = useState(0);

    const maxAvailableDate = useMemo(
        () => getTodayInputValue(),

        [],
    );

    const maxAvailableFtoMonth = useMemo(
        () => getCurrentMonthInputValue(),
        [],
    );

    const maxAvailableMonthlyLateMonth = useMemo(
        () => getCurrentMonthInputValue(),
        [],
    );

    const loadDashboard = useCallback(async () => {
        if (!selectedDate) {
            return;
        }

        setLoading(true);

        setError("");

        try {
            const response = await axios.post(
                `${api.TIME_ATTENDANCE.LATE_ARRIVAL_DASHBOARD}`,

                {
                    date: selectedDate,

                    start: page * PAGE_SIZE,

                    length: PAGE_SIZE,

                    searchKeyword: searchKeyword.trim() || null,

                    ftoYear: Number(String(ftoMonth).slice(0, 4)),

                    ftoMonth: Number(String(ftoMonth).slice(5, 7)),

                    monthlyLateYear: Number(String(monthlyLateMonth).slice(0, 4)),

                    monthlyLateMonth: Number(String(monthlyLateMonth).slice(5, 7)),
                },
            );

            const data = response?.data || {};

            const receivedRows = data.records || [];

            setRows(receivedRows);

            setTrend(data.trend || []);


            setMonthlyEmployeeLateFrequency(
                data.monthlyEmployeeLateFrequency || [],
            );

            const receivedFtoOverview = data.ftoOverview || {};
            setFtoOverview({
                totalFtoCount: Number(receivedFtoOverview.totalFtoCount || 0),
                totalFtoDays: Number(receivedFtoOverview.totalFtoDays || 0),
                employeesWithFto: Number(receivedFtoOverview.employeesWithFto || 0),
                approvedFtoCount: Number(receivedFtoOverview.approvedFtoCount || 0),
                pendingFtoCount: Number(receivedFtoOverview.pendingFtoCount || 0),
                employees: receivedFtoOverview.employees || [],
                records: receivedFtoOverview.records || [],
            });

            setAbsentWithoutFtoEmployees(data.absentWithoutFtoEmployees || []);

            setTotalRecords(Number(data.totalRecords || 0));

            setSummary({
                totalLateArrivals: Number(data.summary?.totalLateArrivals || 0),

                averageLateMinutes: Number(data.summary?.averageLateMinutes || 0),

                maxLateMinutes: Number(data.summary?.maxLateMinutes || 0),

                severeLateCount: Number(data.summary?.severeLateCount || 0),

                moderateLateCount: Number(data.summary?.moderateLateCount || 0),

                under15LateCount: Number(data.summary?.under15LateCount || 0),
            });

            /*
              
                       * The API sorts descending by LateMinutes.
              
                       * Therefore page 0 gives us the actual top
              
                       * late-arrival employees.
              
                       */

            if (page === 0) {
                setTopLateArrivals(receivedRows.slice(0, 5));
            }

            setLastLoadedDate(selectedDate);
        } catch (err) {
            console.error(
                "Late arrival dashboard error:",

                err,
            );

            const message =
                err?.response?.data?.message ||
                err?.response?.data?.title ||
                "Unable to load late arrival data.";

            setError(message);

            setRows([]);

            setTotalRecords(0);

            setTrend([]);


            setMonthlyEmployeeLateFrequency([]);
            setFtoOverview({
                totalFtoCount: 0,
                totalFtoDays: 0,
                employeesWithFto: 0,
                approvedFtoCount: 0,
                pendingFtoCount: 0,
                employees: [],
                records: [],
            });
            setAbsentWithoutFtoEmployees([]);

            if (page === 0) {
                setTopLateArrivals([]);
            }
        } finally {
            setLoading(false);
        }
    }, [selectedDate, page, searchKeyword, ftoMonth, monthlyLateMonth]);

    useEffect(() => {
        const timer = setTimeout(() => {
            loadDashboard();
        }, 350);

        return () => {
            clearTimeout(timer);
        };
    }, [loadDashboard]);

    const filteredFtoEmployees = useMemo(() => {
        const keyword = String(ftoSearch || "").trim().toLowerCase();

        if (!keyword) {
            return ftoOverview?.employees || [];
        }

        return (ftoOverview?.employees || []).filter((employee) =>
            [employee?.employeeName, employee?.employeeId, employee?.userId]
                .filter((value) => value !== null && value !== undefined)
                .some((value) => String(value).toLowerCase().includes(keyword)),
        );
    }, [ftoOverview?.employees, ftoSearch]);

    const filteredAbsentWithoutFtoEmployees = useMemo(() => {
        const keyword = String(absentWithoutFtoSearch || "").trim().toLowerCase();

        if (!keyword) {
            return absentWithoutFtoEmployees || [];
        }

        return (absentWithoutFtoEmployees || []).filter((employee) =>
            [employee?.employeeName, employee?.personId, employee?.organization]
                .filter((value) => value !== null && value !== undefined)
                .some((value) => String(value).toLowerCase().includes(keyword)),
        );
    }, [absentWithoutFtoEmployees, absentWithoutFtoSearch]);

    const paginatedFtoEmployees = useMemo(() => {
        const startIndex = ftoPage * SECTION_PAGE_SIZE;
        return filteredFtoEmployees.slice(
            startIndex,
            startIndex + SECTION_PAGE_SIZE,
        );
    }, [filteredFtoEmployees, ftoPage]);

    const paginatedAbsentWithoutFtoEmployees = useMemo(() => {
        const startIndex = absentWithoutFtoPage * SECTION_PAGE_SIZE;
        return filteredAbsentWithoutFtoEmployees.slice(
            startIndex,
            startIndex + SECTION_PAGE_SIZE,
        );
    }, [filteredAbsentWithoutFtoEmployees, absentWithoutFtoPage]);

    useEffect(() => {
        const maxPage = Math.max(0, Math.ceil(filteredFtoEmployees.length / SECTION_PAGE_SIZE) - 1);
        if (ftoPage > maxPage) {
            setFtoPage(maxPage);
        }
    }, [filteredFtoEmployees.length, ftoPage]);

    useEffect(() => {
        const maxPage = Math.max(0, Math.ceil(filteredAbsentWithoutFtoEmployees.length / SECTION_PAGE_SIZE) - 1);
        if (absentWithoutFtoPage > maxPage) {
            setAbsentWithoutFtoPage(maxPage);
        }
    }, [filteredAbsentWithoutFtoEmployees.length, absentWithoutFtoPage]);

    const handleFtoDetails = (employee) => {
        if (!employee?.userId) {
            return;
        }

        setSelectedFtoEmployee(employee);
        setFtoDetailsOpen(true);
    };

    const handleCloseFtoDetails = () => {
        setFtoDetailsOpen(false);
        setSelectedFtoEmployee(null);
    };

    const loadEmployeeDetails = useCallback(
        async (row) => {
            if (!row?.personId || !selectedDate) {
                return;
            }

            setSelectedEmployee(row);

            setDetailsOpen(true);

            setDetailsLoading(true);

            setDetailsError("");

            setEmployeeDetails(null);

            try {
                const response = await axios.post(
                    LATE_ARRIVAL_EMPLOYEE_DETAILS_ENDPOINT,

                    {
                        personId: Number(row.personId),

                        selectedDate,
                    },
                );

                const rawDetails = response?.data || {};

                setEmployeeDetails(rawDetails);
            } catch (err) {
                console.error(
                    "Late arrival employee details error:",

                    err,
                );

                const message =
                    err?.response?.data?.message ||
                    err?.response?.data?.title ||
                    "Unable to load employee late-arrival details.";

                setDetailsError(message);
            } finally {
                setDetailsLoading(false);
            }
        },

        [selectedDate],
    );

    const handleEmployeeClick = (row) => {
        loadEmployeeDetails(row);
    };

    const handleCloseDetails = () => {
        setDetailsOpen(false);

        setDetailsError("");

        setSelectedEmployee(null);

        setEmployeeDetails(null);
    };

    const handleDateChange = (event) => {
        const nextDate = event.target.value;

        if (!nextDate) {
            return;
        }

        if (nextDate > getTodayInputValue()) {
            return;
        }

        setSelectedDate(nextDate);

        setPage(0);
        setAbsentWithoutFtoPage(0);
    };

    const handlePreviousDay = () => {
        if (!selectedDate) {
            return;
        }

        setSelectedDate(addDays(selectedDate, -1));

        setPage(0);
        setAbsentWithoutFtoPage(0);
    };

    const handleNextDay = () => {
        if (!selectedDate) {
            return;
        }

        const nextDate = addDays(
            selectedDate,

            1,
        );

        if (nextDate > maxAvailableDate) {
            return;
        }

        setSelectedDate(nextDate);

        setPage(0);
        setAbsentWithoutFtoPage(0);
    };

    const handleSearchChange = (event) => {
        setSearchKeyword(event.target.value);

        setPage(0);
    };

    const handleFtoMonthChange = (event) => {
        const nextMonth = event.target.value;

        if (!nextMonth || nextMonth > maxAvailableFtoMonth) {
            return;
        }

        setFtoMonth(nextMonth);
        setFtoPage(0);
        setSelectedFtoEmployee(null);
        setFtoDetailsOpen(false);
    };

    const handleMonthlyLateMonthChange = (event) => {
        const nextMonth = event.target.value;

        if (!nextMonth || nextMonth > maxAvailableMonthlyLateMonth) {
            return;
        }

        setMonthlyLateMonth(nextMonth);
    };

    const handleRefresh = () => {
        loadDashboard();
    };

    const totalLate = summary.totalLateArrivals;

    const averageDelay = summary.averageLateMinutes;

    const maxDelay = summary.maxLateMinutes;

    const maxDelayEmployee = topLateArrivals?.[0]?.employeeName || "-";

    const averageIntensity = Math.min(
        100,

        Math.max(
            0,

            (averageDelay / 30) * 100,
        ),
    );

    const monthlySortedEmployees = useMemo(
        () =>
            [...(monthlyEmployeeLateFrequency || [])].sort((a, b) => {
                const countDiff =
                    getNumericValue(b?.lateArrivalCount) -
                    getNumericValue(a?.lateArrivalCount);

                if (countDiff !== 0) {
                    return countDiff;
                }

                return String(a?.employeeName || "").localeCompare(
                    String(b?.employeeName || ""),
                );
            }),
        [monthlyEmployeeLateFrequency],
    );

    const monthlyTotalLateRecords = useMemo(
        () =>
            monthlySortedEmployees.reduce(
                (sum, item) => sum + getNumericValue(item?.lateArrivalCount),
                0,
            ),
        [monthlySortedEmployees],
    );

    const monthlyRepeatEmployees = useMemo(
        () =>
            monthlySortedEmployees.filter(
                (item) => getNumericValue(item?.lateArrivalCount) > 1,
            ).length,
        [monthlySortedEmployees],
    );

    const monthlyAverageOccurrences = useMemo(
        () =>
            monthlySortedEmployees.length > 0
                ? monthlyTotalLateRecords / monthlySortedEmployees.length
                : 0,
        [monthlySortedEmployees, monthlyTotalLateRecords],
    );

    const topMonthlyEmployee = monthlySortedEmployees[0] || null;

    const employeeCurrentMonthHistory = useMemo(() => {
        if (!employeeDetails?.history?.length || !selectedDate) {
            return [];
        }

        const selectedMonthKey = String(selectedDate).slice(0, 7);

        return employeeDetails.history.filter((item) => {
            const rawDate =
                item?.date ??
                item?.Date ??
                item?.attendanceDate ??
                item?.AttendanceDate;

            return getDateMonthKey(rawDate) === selectedMonthKey;
        });
    }, [employeeDetails, selectedDate]);

    const directorInsight =
        summary.severeLateCount > 0
            ? `${summary.severeLateCount} employee${summary.severeLateCount === 1 ? "" : "s"
            } reached the 30-minute severe-late threshold and may require attention.`
            : totalLate > 0
                ? "No employee crossed the 30-minute severe-late threshold for this date."
                : "No late-arrival records are available for the selected date.";

    return (
        <Box
            sx={{
                width: "100%",

                px: {
                    xs: 1,

                    sm: 2,

                    md: 2.5,
                },

                py: {
                    xs: 1.5,

                    md: 2.25,
                },
            }}
        >
            {/* =========================================================

          HEADER

         ========================================================= */}

            <Stack
                direction={{
                    xs: "column",

                    md: "row",
                }}
                justifyContent="space-between"
                alignItems={{
                    xs: "flex-start",

                    md: "center",
                }}
                spacing={1.5}
                sx={{ mb: 2 }}
            >
                <Box>
                    <Typography
                        sx={{
                            fontSize: {
                                xs: 19,

                                md: 22,
                            },

                            fontWeight: 800,

                            letterSpacing: "-0.02em",
                        }}
                    >
                        Attendance Dashboard
                    </Typography>

                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.35 }}>
                        Executive view of late arrivals for the selected attendance date.
                    </Typography>
                </Box>

                <Button
                    variant="outlined"
                    startIcon={
                        loading ? <CircularProgress size={16} /> : <RefreshRounded />
                    }
                    onClick={handleRefresh}
                    disabled={loading}
                    sx={{
                        borderRadius: 2,

                        minWidth: 105,

                        textTransform: "none",

                        fontWeight: 700,
                    }}
                >
                    Refresh
                </Button>
            </Stack>

            {/* =========================================================

          DATE SELECTOR

         ========================================================= */}

            <Paper
                elevation={0}
                sx={{
                    mb: 2.25,

                    p: {
                        xs: 1.25,

                        sm: 1.5,
                    },

                    border: "1px solid",

                    borderColor: "divider",

                    borderRadius: 3,
                }}
            >
                <Stack
                    direction={{
                        xs: "column",

                        sm: "row",
                    }}
                    alignItems={{
                        xs: "stretch",

                        sm: "center",
                    }}
                    spacing={1.25}
                >
                    <Tooltip title="Previous day">
                        <span>
                            <IconButton
                                onClick={handlePreviousDay}
                                disabled={loading}
                                sx={{
                                    border: "1px solid",

                                    borderColor: "divider",
                                }}
                            >
                                <ChevronLeftRounded />
                            </IconButton>
                        </span>
                    </Tooltip>

                    <TextField
                        type="date"
                        label="Attendance Date"
                        value={selectedDate}
                        onChange={handleDateChange}
                        size="small"
                        InputLabelProps={{
                            shrink: true,
                        }}
                        inputProps={{
                            max: maxAvailableDate,
                        }}
                        sx={{
                            minWidth: {
                                xs: "100%",

                                sm: 220,
                            },
                        }}
                    />

                    <Tooltip title="Next available day">
                        <span>
                            <IconButton
                                onClick={handleNextDay}
                                disabled={loading || selectedDate >= maxAvailableDate}
                                sx={{
                                    border: "1px solid",

                                    borderColor: "divider",
                                }}
                            >
                                <ChevronRightRounded />
                            </IconButton>
                        </span>
                    </Tooltip>

                    <Box
                        sx={{
                            display: {
                                xs: "none",

                                sm: "block",
                            },

                            flex: 1,
                        }}
                    />

                    <Box>
                        <Typography variant="caption" color="text.secondary">
                            Viewing
                        </Typography>

                        <Typography variant="body2" sx={{ fontWeight: 800 }}>
                            {formatSelectedDate(selectedDate)}
                        </Typography>
                    </Box>
                </Stack>
            </Paper>

            {error && (
                <Alert
                    severity="error"
                    onClose={() => setError("")}
                    sx={{
                        mb: 2,

                        borderRadius: 2.5,
                    }}
                >
                    {error}
                </Alert>
            )}

            {/* =========================================================

          KPI CARDS

         ========================================================= */}

            <Box
                sx={{
                    display: "grid",

                    gridTemplateColumns: {
                        xs: "1fr",

                        sm: "repeat(2, 1fr)",

                        lg: "repeat(4, 1fr)",
                    },

                    gap: 1.5,

                    mb: 2,
                }}
            >
                <StatCard
                    title="Late Arrivals"
                    value={totalLate}
                    subtitle="Records matching 08:00–08:30"
                    icon={<GroupsRounded />}
                    accent="#3f51b5"
                />

                <StatCard
                    title="Average Delay"
                    value={`${averageDelay.toFixed(2)} min`}
                    subtitle="Across selected date"
                    icon={<AccessTimeRounded />}
                    accent="#00897b"
                />

                <StatCard
                    title="Maximum Delay"
                    value={formatMinutes(maxDelay)}
                    subtitle={
                        maxDelayEmployee !== "-" ? maxDelayEmployee : "No late arrivals"
                    }
                    icon={<TrendingUpRounded />}
                    accent="#f57c00"
                />

                <StatCard
                    title="Severe Late"
                    value={summary.severeLateCount}
                    subtitle="30 minutes or more"
                    icon={<WarningAmberRounded />}
                    accent="#d32f2f"
                />
            </Box>

            {/* =========================================================

          EXECUTIVE ANALYSIS

         ========================================================= */}

            <Box
                sx={{
                    display: "grid",

                    gridTemplateColumns: {
                        xs: "1fr",

                        lg: "1.1fr 1fr",
                    },

                    gap: 1.5,

                    mb: 2,
                }}
            >
                {/* Severity */}

                <Card
                    elevation={0}
                    sx={{
                        border: "1px solid",

                        borderColor: "divider",

                        borderRadius: 3,
                    }}
                >
                    <CardContent sx={{ p: 2.25 }}>
                        <Stack
                            direction="row"
                            alignItems="center"
                            spacing={1}
                            sx={{ mb: 2.1 }}
                        >
                            <Box
                                sx={{
                                    width: 34,

                                    height: 34,

                                    borderRadius: 1.75,

                                    display: "flex",

                                    alignItems: "center",

                                    justifyContent: "center",

                                    backgroundColor: "#f3f5ff",

                                    color: "#3f51b5",
                                }}
                            >
                                <AssessmentRounded fontSize="small" />
                            </Box>

                            <Box>
                                <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
                                    Late Arrival Severity
                                </Typography>

                                <Typography variant="caption" color="text.secondary">
                                    Distribution of delays
                                </Typography>
                            </Box>
                        </Stack>

                        <SeverityDonut
                            under15={summary.under15LateCount}
                            moderate={summary.moderateLateCount}
                            severe={summary.severeLateCount}
                            centerLabel="Late"
                            centerValue={totalLate}
                        />

                        <Divider sx={{ my: 1.25 }} />

                        <SeverityRow
                            label="Under 15 minutes"
                            count={summary.under15LateCount}
                            total={totalLate}
                            progressColor="#42a5f5"
                            description="Minor delay"
                        />

                        <SeverityRow
                            label="15–29 minutes"
                            count={summary.moderateLateCount}
                            total={totalLate}
                            progressColor="#ff9800"
                            description="Requires attention"
                        />

                        <SeverityRow
                            label="30+ minutes"
                            count={summary.severeLateCount}
                            total={totalLate}
                            progressColor="#ef5350"
                            description="Severe delay"
                        />
                    </CardContent>
                </Card>

                {/* Top late arrivals */}

                <Card
                    elevation={0}
                    sx={{
                        border: "1px solid",

                        borderColor: "divider",

                        borderRadius: 3,
                    }}
                >
                    <CardContent sx={{ p: 2.25 }}>
                        <Stack
                            direction="row"
                            justifyContent="space-between"
                            alignItems="flex-start"
                            sx={{ mb: 1 }}
                        >
                            <Box>
                                <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
                                    Top Late Arrivals
                                </Typography>

                                <Typography variant="caption" color="text.secondary">
                                    Employees with the highest recorded delay
                                </Typography>
                            </Box>

                            <TrendingUpRounded
                                sx={{
                                    color: "#f57c00",
                                }}
                            />
                        </Stack>

                        <Divider sx={{ mb: 0.5 }} />

                        {topLateArrivals.length === 0 ? (
                            <Box
                                sx={{
                                    py: 5,

                                    textAlign: "center",
                                }}
                            >
                                <Typography variant="body2" color="text.secondary">
                                    No late arrival data available.
                                </Typography>
                            </Box>
                        ) : (
                            topLateArrivals.map((row, index) => (
                                <TopLateEmployee
                                    key={`${row.personId}-${row.attendanceDate}-${index}`}
                                    row={row}
                                    rank={index + 1}
                                    maxLate={maxDelay}
                                    onClick={() => handleEmployeeClick(row)}
                                />
                            ))
                        )}
                    </CardContent>
                </Card>
            </Box>

            {/* =========================================================
              MANAGEMENT ANALYTICS
             ========================================================= */}
            <Box
                sx={{
                    display: "grid",
                    gridTemplateColumns: {
                        xs: "1fr",
                        lg: "1.35fr 0.9fr",
                    },
                    gap: 1.5,
                    mb: 2,
                }}
            >
                <Card
                    elevation={0}
                    sx={{
                        border: "1px solid",
                        borderColor: "divider",
                        borderRadius: 3,
                    }}
                >
                    <CardContent sx={{ p: 2.25 }}>
                        <Stack
                            direction="row"
                            justifyContent="space-between"
                            alignItems="flex-start"
                            sx={{ mb: 1.1 }}
                        >
                            <Box>
                                <Typography variant="subtitle1" sx={{ fontWeight: 850 }}>
                                    14-Day Late Arrival Trend
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                    Daily late-arrival volume and severe cases
                                </Typography>
                            </Box>

                            <Chip
                                size="small"
                                variant="outlined"
                                label={`${trend.length} available day${trend.length === 1 ? "" : "s"
                                    }`}
                            />
                        </Stack>

                        <TrendChart
                            data={trend}
                            series={[
                                {
                                    key: "lateArrivalCount",
                                    label: "Late arrivals",
                                    color: "#42a5f5",
                                },
                                {
                                    key: "severeLateCount",
                                    label: "Severe 30+",
                                    color: "#ef5350",
                                },
                            ]}
                        />

                        <Divider sx={{ my: 1.25 }} />

                        <Stack direction="row" spacing={0.8} flexWrap="wrap" useFlexGap>
                            <Chip
                                size="small"
                                label={`Selected day: ${totalLate}`}
                                variant="outlined"
                            />
                            <Chip
                                size="small"
                                label={`Avg delay: ${averageDelay.toFixed(2)} min`}
                                variant="outlined"
                            />
                            <Chip
                                size="small"
                                label={`Severe: ${summary.severeLateCount}`}
                                color={summary.severeLateCount > 0 ? "error" : "default"}
                            />
                        </Stack>
                    </CardContent>
                </Card>

                <Card
                    elevation={0}
                    sx={{
                        border: "1px solid",
                        borderColor: "divider",
                        borderRadius: 3,
                    }}
                >
                    <CardContent sx={{ p: 2.25 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 850 }}>
                            Monthly Late Arrival Overview
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            Repeat lateness for the selected month
                        </Typography>

                        <Box
                            sx={{
                                mt: 1.6,
                                display: "grid",
                                gridTemplateColumns: {
                                    xs: "repeat(2, 1fr)",
                                    sm: "repeat(2, 1fr)",
                                },
                                gap: 1,
                            }}
                        >
                            {[
                                ["Late employees", monthlySortedEmployees.length],
                                ["Late occurrences", monthlyTotalLateRecords],
                                ["Repeat employees", monthlyRepeatEmployees],
                                ["Avg times / employee", monthlyAverageOccurrences.toFixed(1)],
                            ].map(([label, value]) => (
                                <Paper
                                    key={label}
                                    elevation={0}
                                    sx={{
                                        p: 1.35,
                                        border: "1px solid",
                                        borderColor: "divider",
                                        borderRadius: 2,
                                        backgroundColor: "background.default",
                                    }}
                                >
                                    <Typography variant="caption" color="text.secondary">
                                        {label}
                                    </Typography>
                                    <Typography
                                        sx={{
                                            mt: 0.3,
                                            fontSize: 20,
                                            fontWeight: 900,
                                        }}
                                    >
                                        {value}
                                    </Typography>
                                </Paper>
                            ))}
                        </Box>

                        <Divider sx={{ my: 1.35 }} />

                        <Typography variant="caption" color="text.secondary">
                            Most frequent this month
                        </Typography>

                        <Stack
                            direction="row"
                            alignItems="center"
                            justifyContent="space-between"
                            spacing={1}
                            sx={{ mt: 0.55 }}
                        >
                            <Box sx={{ minWidth: 0 }}>
                                <Typography
                                    variant="body2"
                                    sx={{
                                        fontWeight: 850,
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                        whiteSpace: "nowrap",
                                    }}
                                >
                                    {topMonthlyEmployee?.employeeName || "No late employees"}
                                </Typography>
                                {topMonthlyEmployee && (
                                    <Typography variant="caption" color="text.secondary">
                                        ID: {topMonthlyEmployee.personId || "-"} • Avg{" "}
                                        {getNumericValue(
                                            topMonthlyEmployee.averageLateMinutes,
                                        ).toFixed(1)}{" "}
                                        min
                                    </Typography>
                                )}
                            </Box>

                            <Chip
                                size="small"
                                color={topMonthlyEmployee ? "error" : "default"}
                                label={
                                    topMonthlyEmployee
                                        ? `${getNumericValue(
                                            topMonthlyEmployee.lateArrivalCount,
                                        )} times`
                                        : "0 times"
                                }
                                sx={{ fontWeight: 850, flexShrink: 0 }}
                            />
                        </Stack>
                    </CardContent>
                </Card>
            </Box>

            {/* =========================================================
          MONTHLY EMPLOYEE LATE FREQUENCY
         ========================================================= */}
            <Card
                elevation={0}
                sx={{
                    mb: 2,
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: 3,
                }}
            >
                <CardContent sx={{ p: 2.25 }}>
                    <Stack
                        direction={{ xs: "column", lg: "row" }}
                        justifyContent="space-between"
                        alignItems={{ xs: "stretch", lg: "center" }}
                        spacing={1.25}
                        sx={{ mb: 1.35 }}
                    >
                        <Box>
                            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                                <Typography variant="subtitle1" sx={{ fontWeight: 850 }}>
                                    Monthly Late Employee Frequency
                                </Typography>
                                <Chip
                                    size="small"
                                    variant="outlined"
                                    label={`Month: ${getFtoMonthLabel(monthlyLateMonth)}`}
                                />
                            </Stack>
                            <Typography variant="caption" color="text.secondary">
                                Date-wise late-arrival frequency for the selected month.
                            </Typography>
                        </Box>

                        <Stack
                            direction={{ xs: "column", sm: "row" }}
                            alignItems={{ xs: "stretch", sm: "center" }}
                            spacing={1}
                        >
                            <TextField
                                type="month"
                                label="Late Arrival Month"
                                value={monthlyLateMonth}
                                onChange={handleMonthlyLateMonthChange}
                                size="small"
                                inputProps={{ max: maxAvailableMonthlyLateMonth }}
                                InputLabelProps={{ shrink: true }}
                                sx={{ width: { xs: "100%", sm: 175 } }}
                            />

                            <TextField
                                value={monthlyEmployeeSearch}
                                onChange={(event) =>
                                    setMonthlyEmployeeSearch(event.target.value)
                                }
                                placeholder="Search employee or ID"
                                size="small"
                                sx={{
                                    width: {
                                        xs: "100%",
                                        sm: 245,
                                    },
                                }}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <SearchRounded fontSize="small" />
                                        </InputAdornment>
                                    ),
                                }}
                            />

                            <Chip
                                size="small"
                                variant="outlined"
                                label={`${monthlyEmployeeLateFrequency.length} employee${monthlyEmployeeLateFrequency.length === 1 ? "" : "s"
                                    }`}
                                sx={{ alignSelf: { xs: "flex-start", sm: "center" } }}
                            />
                        </Stack>
                    </Stack>

                    <MonthlyLateEmployeeList
                        data={monthlyEmployeeLateFrequency}
                        searchKeyword={monthlyEmployeeSearch}
                        onSearchChange={setMonthlyEmployeeSearch}
                        onViewDetails={handleEmployeeClick}
                        paginationResetKey={monthlyLateMonth}
                    />
                </CardContent>
            </Card>

            {/* =========================================================
          CURRENT MONTH FTO OVERVIEW
         ========================================================= */}
            <Card
                elevation={0}
                sx={{
                    mb: 2,
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: 3,
                    overflow: "hidden",
                }}
            >
                <CardContent sx={{ p: 2.25 }}>
                    <Stack
                        direction={{ xs: "column", lg: "row" }}
                        justifyContent="space-between"
                        alignItems={{ xs: "stretch", lg: "center" }}
                        spacing={1.25}
                        sx={{ mb: 1.35 }}
                    >
                        <Box>
                            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                                <Typography variant="subtitle1" sx={{ fontWeight: 850 }}>
                                    FTO Overview
                                </Typography>
                                <Chip
                                    size="small"
                                    variant="outlined"
                                    label={`Month: ${getFtoMonthLabel(ftoMonth)}`}
                                />
                            </Stack>
                            <Typography variant="caption" color="text.secondary">
                                FTO usage for the selected month with current leave balance.
                            </Typography>
                        </Box>

                        <Stack
                            direction={{ xs: "column", sm: "row" }}
                            spacing={1}
                            alignItems={{ xs: "stretch", sm: "center" }}
                        >
                            <TextField
                                type="month"
                                label="FTO Month"
                                value={ftoMonth}
                                onChange={handleFtoMonthChange}
                                size="small"
                                inputProps={{ max: maxAvailableFtoMonth }}
                                InputLabelProps={{ shrink: true }}
                                sx={{ width: { xs: "100%", sm: 175 } }}
                            />

                            <TextField
                                value={ftoSearch}
                                onChange={(event) => {
                                    setFtoSearch(event.target.value);
                                    setFtoPage(0);
                                }}
                                placeholder="Search employee or ID"
                                size="small"
                                sx={{ width: { xs: "100%", sm: 245 } }}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <SearchRounded fontSize="small" />
                                        </InputAdornment>
                                    ),
                                }}
                            />
                        </Stack>
                    </Stack>

                    <Box
                        sx={{
                            display: "grid",
                            gridTemplateColumns: {
                                xs: "repeat(2, 1fr)",
                                sm: "repeat(4, 1fr)",
                            },
                            gap: 1,
                            mb: 1.5,
                        }}
                    >
                        {[
                            ["FTO Requests", ftoOverview.totalFtoCount],
                            ["FTO Days", Number(ftoOverview.totalFtoDays || 0).toFixed(2)],
                            ["Employees", ftoOverview.employeesWithFto],
                            ["Pending", ftoOverview.pendingFtoCount],
                        ].map(([label, value]) => (
                            <Paper
                                key={label}
                                elevation={0}
                                sx={{
                                    p: 1.35,
                                    border: "1px solid",
                                    borderColor: "divider",
                                    borderRadius: 2,
                                    backgroundColor: "background.default",
                                }}
                            >
                                <Typography variant="caption" color="text.secondary">
                                    {label}
                                </Typography>
                                <Typography sx={{ mt: 0.3, fontSize: 20, fontWeight: 900 }}>
                                    {value}
                                </Typography>
                            </Paper>
                        ))}
                    </Box>

                    <Divider sx={{ mb: 1.15 }} />

                    <TableContainer sx={LIST_TABLE_CONTAINER_SX}>
                        <Table
                            size="small"
                            stickyHeader
                            sx={{
                                "& .MuiTableCell-root": {
                                    py: 0.65,
                                },
                            }}
                        >
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={{ fontWeight: 800, width: 55 }}>#</TableCell>
                                    <TableCell sx={{ fontWeight: 800 }}>Employee</TableCell>
                                    <TableCell sx={{ fontWeight: 800 }}>FTO Count</TableCell>
                                    <TableCell sx={{ fontWeight: 800 }}>FTO Days</TableCell>
                                    <TableCell sx={{ fontWeight: 800 }}>Current Leave Balance</TableCell>
                                    <TableCell sx={{ fontWeight: 800, width: 110 }}>Action</TableCell>
                                </TableRow>
                            </TableHead>

                            <TableBody>
                                {filteredFtoEmployees.length ? (
                                    paginatedFtoEmployees.map((employee, index) => {
                                        return (
                                            <TableRow
                                                key={`fto-employee-${employee?.userId}-${index}`}
                                                hover
                                            >
                                                <TableCell>{ftoPage * SECTION_PAGE_SIZE + index + 1}</TableCell>
                                                <TableCell sx={{ minWidth: 200, py: 0.65 }}>
                                                    <Typography variant="body2" sx={{ fontWeight: 800, lineHeight: 1.2 }}>
                                                        {employee?.employeeName || "-"}
                                                    </Typography>
                                                    <Typography
                                                        variant="caption"
                                                        color="text.secondary"
                                                        sx={{ lineHeight: 1.1 }}
                                                    >
                                                        ID: {employee?.employeeId || employee?.userId || "-"}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell sx={{ fontWeight: 850 }}>
                                                    {Number(employee?.ftoCount || 0)}
                                                </TableCell>
                                                <TableCell sx={{ fontWeight: 850 }}>
                                                    {Number(employee?.totalFtoDays || 0).toFixed(2)}
                                                </TableCell>
                                                <TableCell sx={{ py: 0.65 }}>
                                                    <Stack direction="row" spacing={0.35} flexWrap="nowrap" useFlexGap>
                                                        <Chip
                                                            size="small"
                                                            label={`EL ${Number(employee?.elBalance ?? 0).toFixed(1)}`}
                                                        sx={{ height: 24, fontSize: 11 }}
                                                        />
                                                        <Chip
                                                            size="small"
                                                            label={`CL ${Number(employee?.clBalance ?? 0).toFixed(1)}`}
                                                        sx={{ height: 24, fontSize: 11 }}
                                                        />
                                                        <Chip
                                                            size="small"
                                                            label={`SL ${Number(employee?.slBalance ?? 0).toFixed(1)}`}
                                                        sx={{ height: 24, fontSize: 11 }}
                                                        />
                                                    </Stack>
                                                </TableCell>
                                                <TableCell sx={{ py: 0.65, whiteSpace: "nowrap" }}>
                                                    <Button
                                                        size="small"
                                                        variant="outlined"
                                                        onClick={() => handleFtoDetails(employee)}
                                                        sx={{
                                                            textTransform: "none",
                                                            fontWeight: 800,
                                                            whiteSpace: "nowrap",
                                                            minWidth: 96,
                                                            py: 0.45,
                                                            px: 1.1,
                                                            lineHeight: 1.2,
                                                        }}
                                                    >
                                                        View Details
                                                    </Button>
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })
                                ) : (
                                    <TableRow>
                                        <TableCell colSpan={6} align="center" sx={{ py: 3 }}>
                                            <Typography variant="body2" color="text.secondary">
                                                No FTO records found for {getFtoMonthLabel(ftoMonth)}.
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    <Divider sx={{ mt: 1 }} />

                    <TablePagination
                        component="div"
                        count={filteredFtoEmployees.length}
                        page={ftoPage}
                        onPageChange={(_, newPage) => setFtoPage(newPage)}
                        rowsPerPage={SECTION_PAGE_SIZE}
                        rowsPerPageOptions={[SECTION_PAGE_SIZE]}
                        labelRowsPerPage="Rows:"
                    />
                </CardContent>
            </Card>

            {/* =========================================================
          ABSENT WITHOUT FTO
         ========================================================= */}
            <Card
                elevation={0}
                sx={{
                    mb: 2,
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: 3,
                    overflow: "hidden",
                }}
            >
                <CardContent sx={{ p: 2.25 }}>
                    <Stack
                        direction={{ xs: "column", lg: "row" }}
                        justifyContent="space-between"
                        alignItems={{ xs: "stretch", lg: "center" }}
                        spacing={1.25}
                        sx={{ mb: 1.35 }}
                    >
                        <Box>
                            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                                <Typography variant="subtitle1" sx={{ fontWeight: 850 }}>
                                    Absent Without FTO
                                </Typography>
                                <Chip
                                    size="small"
                                    variant="outlined"
                                    label={`Month: ${getFtoMonthLabel(String(selectedDate).slice(0, 7))}`}
                                />
                            </Stack>
                            <Typography variant="caption" color="text.secondary">
                                Office employees marked Absent with no FTO record on the selected date.
                                Saturday and Sunday are not considered.
                            </Typography>
                        </Box>

                        <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems={{ xs: "stretch", sm: "center" }}>
                            <TextField
                                value={absentWithoutFtoSearch}
                                onChange={(event) => {
                                    setAbsentWithoutFtoSearch(event.target.value);
                                    setAbsentWithoutFtoPage(0);
                                }}
                                placeholder="Search employee or ID"
                                size="small"
                                sx={{ width: { xs: "100%", sm: 245 } }}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <SearchRounded fontSize="small" />
                                        </InputAdornment>
                                    ),
                                }}
                            />
                            <Chip
                                size="small"
                                variant="outlined"
                                color={filteredAbsentWithoutFtoEmployees.length ? "error" : "default"}
                                label={`${filteredAbsentWithoutFtoEmployees.length} employee${filteredAbsentWithoutFtoEmployees.length === 1 ? "" : "s"}`}
                                sx={{ alignSelf: { xs: "flex-start", sm: "center" } }}
                            />
                        </Stack>
                    </Stack>

                    {(() => {
                        const selectedDateObject = new Date(`${selectedDate}T00:00:00`);
                        const isWeekend =
                            selectedDateObject.getDay() === 0 ||
                            selectedDateObject.getDay() === 6;

                        if (isWeekend) {
                            return (
                                <Alert severity="info" icon={false} sx={{ borderRadius: 2 }}>
                                    Saturday and Sunday are holidays and are excluded from the Absent Without FTO check.
                                </Alert>
                            );
                        }

                        if (!filteredAbsentWithoutFtoEmployees.length) {
                            return (
                                <Box sx={{ py: 3, textAlign: "center" }}>
                                    <Typography variant="body2" color="text.secondary">
                                        No employees are marked Absent without an FTO on this date.
                                    </Typography>
                                </Box>
                            );
                        }

                        return (
                            <>
                                <TableContainer sx={LIST_TABLE_CONTAINER_SX}>
                                    <Table size="small" stickyHeader>
                                        <TableHead>
                                            <TableRow>
                                                <TableCell sx={{ fontWeight: 800, width: 55 }}>#</TableCell>
                                                <TableCell sx={{ fontWeight: 800 }}>Date</TableCell>
                                                <TableCell sx={{ fontWeight: 800 }}>Employee</TableCell>
                                                <TableCell sx={{ fontWeight: 800 }}>Person ID</TableCell>
                                                <TableCell sx={{ fontWeight: 800 }}>Organization</TableCell>
                                                <TableCell sx={{ fontWeight: 800 }}>Attendance</TableCell>
                                                <TableCell sx={{ fontWeight: 800 }}>FTO</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {paginatedAbsentWithoutFtoEmployees.map((employee, index) => (
                                                <TableRow
                                                    key={`absent-no-fto-${employee?.personId}-${index}`}
                                                    hover
                                                    sx={{
                                                        backgroundColor: "rgba(239,83,80,0.045)",
                                                        "&:hover": {
                                                            backgroundColor: "rgba(239,83,80,0.08)",
                                                        },
                                                    }}
                                                >
                                                    <TableCell>{absentWithoutFtoPage * SECTION_PAGE_SIZE + index + 1}</TableCell>
                                                    <TableCell>
                                                        <Typography variant="body2" sx={{ fontWeight: 800 }}>
                                                            {getRawDateOnly(employee?.attendanceDate || selectedDate)}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell>
                                                        <Typography variant="body2" sx={{ fontWeight: 800, color: "error.main" }}>
                                                            {employee?.employeeName || "-"}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell>{employee?.personId || "-"}</TableCell>
                                                    <TableCell>{employee?.organization || "-"}</TableCell>
                                                    <TableCell>
                                                        <Chip size="small" label="Absent" color="error" />
                                                    </TableCell>
                                                    <TableCell>
                                                        <Chip size="small" label="No FTO Created" color="warning" variant="outlined" />
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>

                                <Divider sx={{ mt: 1 }} />

                                <TablePagination
                                    component="div"
                                    count={filteredAbsentWithoutFtoEmployees.length}
                                    page={absentWithoutFtoPage}
                                    onPageChange={(_, newPage) => setAbsentWithoutFtoPage(newPage)}
                                    rowsPerPage={SECTION_PAGE_SIZE}
                                    rowsPerPageOptions={[SECTION_PAGE_SIZE]}
                                    labelRowsPerPage="Rows:"
                                />
                            </>
                        );
                    })()}
                </CardContent>
            </Card>

            {/* =========================================================

          DETAIL TABLE

         ========================================================= */}

            <Card
                elevation={0}
                sx={{
                    border: "1px solid",

                    borderColor: "divider",

                    borderRadius: 3,

                    overflow: "hidden",
                }}
            >
                <CardContent
                    sx={{
                        p: 0,

                        "&:last-child": {
                            pb: 0,
                        },
                    }}
                >
                    {/* Table header */}

                    <Box
                        sx={{
                            px: {
                                xs: 1.5,

                                md: 2.25,
                            },

                            py: 1.75,
                        }}
                    >
                        <Stack
                            direction={{
                                xs: "column",

                                sm: "row",
                            }}
                            justifyContent="space-between"
                            alignItems={{
                                xs: "stretch",

                                sm: "center",
                            }}
                            spacing={1.5}
                        >
                            <Box>
                                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                                <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
                                    Employee Late Arrival Details
                                </Typography>
                                <Chip
                                    size="small"
                                    variant="outlined"
                                    label={`Month: ${getFtoMonthLabel(String(selectedDate).slice(0, 7))}`}
                                />
                            </Stack>
                            <Typography variant="caption" color="text.secondary">
                                {lastLoadedDate
                                    ? `Attendance: ${formatSelectedDate(lastLoadedDate)}`
                                    : "Loading attendance data..."}
                            </Typography>
                                <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{
                                        display: "block",
                                        mt: 0.35,
                                        fontWeight: 600,
                                    }}
                                >
                                    Click an employee or View Details to open their late-arrival
                                    history.
                                </Typography>
                            </Box>

                            <TextField
                                value={searchKeyword}
                                onChange={handleSearchChange}
                                placeholder="Search employee or person ID"
                                size="small"
                                sx={{
                                    width: {
                                        xs: "100%",

                                        sm: 320,
                                    },
                                }}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <SearchRounded fontSize="small" />
                                        </InputAdornment>
                                    ),
                                }}
                            />
                        </Stack>
                    </Box>

                    <Divider />

                    <TableContainer
                        sx={{
                            ...LIST_TABLE_CONTAINER_SX,
                            position: "relative",
                            minHeight: 310,
                        }}
                    >
                        {loading && (
                            <Box
                                sx={{
                                    position: "absolute",

                                    inset: 0,

                                    zIndex: 4,

                                    display: "flex",

                                    alignItems: "center",

                                    justifyContent: "center",

                                    backgroundColor: "rgba(255,255,255,0.62)",

                                    backdropFilter: "blur(1.5px)",
                                }}
                            >
                                <CircularProgress size={30} />
                            </Box>
                        )}

                        <Table stickyHeader size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell
                                        sx={{
                                            fontWeight: 800,

                                            width: 65,
                                        }}
                                    >
                                        #
                                    </TableCell>

                                    <TableCell
                                        sx={{
                                            fontWeight: 800,
                                        }}
                                    >
                                        Employee
                                    </TableCell>

                                    <TableCell
                                        sx={{
                                            fontWeight: 800,
                                        }}
                                    >
                                        Person ID
                                    </TableCell>

                                    <TableCell
                                        sx={{
                                            fontWeight: 800,
                                        }}
                                    >
                                        In Time
                                    </TableCell>

                                    <TableCell
                                        sx={{
                                            fontWeight: 800,
                                        }}
                                    >
                                        Late By
                                    </TableCell>

                                    <TableCell
                                        sx={{
                                            fontWeight: 800,
                                        }}
                                    >
                                        Status
                                    </TableCell>
                                    <TableCell
                                        align="right"
                                        sx={{
                                            fontWeight: 800,
                                            width: 130,
                                        }}
                                    >
                                        Action
                                    </TableCell>
                                </TableRow>
                            </TableHead>

                            <TableBody>
                                {!loading && rows.length === 0 && (
                                    <TableRow>
                                        <TableCell colSpan={7} align="center" sx={{ py: 8 }}>
                                            <Typography
                                                variant="body1"
                                                sx={{
                                                    fontWeight: 700,

                                                    mb: 0.5,
                                                }}
                                            >
                                                No late arrivals found
                                            </Typography>

                                            <Typography variant="body2" color="text.secondary">
                                                No records match the selected date and search criteria.
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                )}

                                {rows.map((row, index) => {
                                    const statusMeta = getStatusMeta(row.lateMinutes);
                                    const isMoreThanTwoMinutesLate = Number(row?.lateMinutes || 0) > 2;

                                    return (
                                        <TableRow
                                            key={`${row.personId}-${row.attendanceDate}-${index}`}
                                            hover
                                            onClick={() => handleEmployeeClick(row)}
                                            sx={{
                                                cursor: "pointer",
                                                ...(isMoreThanTwoMinutesLate
                                                    ? {
                                                        backgroundColor: "rgba(211, 47, 47, 0.035)",
                                                        "&:hover": {
                                                            backgroundColor: "rgba(211, 47, 47, 0.07)",
                                                        },
                                                    }
                                                    : {}),
                                            }}
                                        >
                                            <TableCell>{page * PAGE_SIZE + index + 1}</TableCell>

                                            <TableCell>
                                                <Typography
                                                    variant="body2"
                                                    sx={{
                                                        fontWeight: 700,
                                                    }}
                                                >
                                                    {row.employeeName || "-"}
                                                </Typography>
                                            </TableCell>

                                            <TableCell>{row.personId || "-"}</TableCell>

                                            <TableCell>
                                                <Typography
                                                    variant="body2"
                                                    sx={{
                                                        fontWeight: 700,
                                                    }}
                                                >
                                                    {row.checkIn || "-"}
                                                </Typography>
                                            </TableCell>

                                            <TableCell>
                                                <Typography
                                                    variant="body2"
                                                    sx={{
                                                        fontWeight: 900,
                                                        color: isMoreThanTwoMinutesLate ? "error.main" : "text.primary",
                                                    }}
                                                >
                                                    {formatMinutes(row.lateMinutes)}
                                                </Typography>
                                            </TableCell>

                                            <TableCell>
                                                <Chip
                                                    label={row.status || statusMeta.label}
                                                    color={statusMeta.color}
                                                    size="small"
                                                    sx={{
                                                        fontWeight: 700,
                                                    }}
                                                />
                                            </TableCell>
                                            <TableCell align="right">
                                                <Button
                                                    size="small"
                                                    variant="text"
                                                    onClick={(event) => {
                                                        event.stopPropagation();
                                                        handleEmployeeClick(row);
                                                    }}
                                                    sx={{
                                                        textTransform: "none",
                                                        fontWeight: 800,
                                                        minWidth: 110,
                                                    }}
                                                >
                                                    View Details
                                                </Button>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    <Divider />

                    <TablePagination
                        component="div"
                        count={totalRecords}
                        page={page}
                        onPageChange={(_, newPage) => setPage(newPage)}
                        rowsPerPage={PAGE_SIZE}
                        rowsPerPageOptions={[PAGE_SIZE]}
                        labelRowsPerPage="Rows:"
                    />
                </CardContent>
            </Card>

            <Dialog
                open={detailsOpen}
                onClose={handleCloseDetails}
                fullWidth
                maxWidth="md"
                scroll="paper"
                PaperProps={{
                    sx: {
                        width: "calc(100% - 32px)",
                        maxWidth: 1180,
                        maxHeight: "88vh",
                        borderRadius: 3,
                        overflow: "hidden",
                        boxShadow: "0 24px 70px rgba(0,0,0,0.22)",
                    },
                }}
            >
                <DialogTitle sx={{ p: 0 }}>
                    <Box
                        sx={{
                            px: { xs: 2, sm: 2.5 },
                            py: 1.75,
                            borderBottom: "1px solid",
                            borderColor: "divider",
                            background:
                                "linear-gradient(135deg, rgba(255,126,87,0.08), rgba(63,81,181,0.04))",
                        }}
                    >
                        <Stack direction="row" alignItems="center" spacing={1.5}>
                            <Box
                                sx={{
                                    width: 46,
                                    height: 46,
                                    borderRadius: "50%",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    flexShrink: 0,
                                    backgroundColor: "#fff1ea",
                                    color: "#f36f43",
                                    fontSize: 16,
                                    fontWeight: 900,
                                }}
                            >
                                {(
                                    employeeDetails?.employeeName ||
                                    employeeDetails?.selectedDay?.employeeName ||
                                    selectedEmployee?.employeeName ||
                                    "E"
                                )
                                    .split(" ")
                                    .filter(Boolean)
                                    .slice(0, 2)
                                    .map((part) => part[0])
                                    .join("")
                                    .toUpperCase()}
                            </Box>

                            <Box sx={{ minWidth: 0, flex: 1 }}>
                                <Stack
                                    direction="row"
                                    alignItems="center"
                                    spacing={1}
                                    flexWrap="wrap"
                                    useFlexGap
                                >
                                    <Typography
                                        variant="h6"
                                        sx={{
                                            fontWeight: 900,
                                            fontSize: { xs: 17, sm: 19 },
                                            lineHeight: 1.2,
                                        }}
                                    >
                                        {employeeDetails?.employeeName ||
                                            employeeDetails?.selectedDay?.employeeName ||
                                            selectedEmployee?.employeeName ||
                                            "Employee Details"}
                                    </Typography>

                                    <Chip
                                        size="small"
                                        color={
                                            Number(
                                                employeeDetails?.currentMonth?.lateRecordCount ??
                                                selectedEmployee?.lateArrivalCount ??
                                                0,
                                            ) > 0
                                                ? "error"
                                                : "default"
                                        }
                                        label={`${Number(
                                            employeeDetails?.currentMonth?.lateRecordCount ??
                                            selectedEmployee?.lateArrivalCount ??
                                            0,
                                        )} late ${Number(
                                            employeeDetails?.currentMonth?.lateRecordCount ??
                                            selectedEmployee?.lateArrivalCount ??
                                            0,
                                        ) === 1
                                            ? "time"
                                            : "times"
                                            } this month`}
                                        sx={{ fontWeight: 800 }}
                                    />
                                </Stack>

                                <Typography variant="caption" color="text.secondary">
                                    Person ID:{" "}
                                    {employeeDetails?.personId ||
                                        selectedEmployee?.personId ||
                                        "-"}{" "}
                                    • {formatSelectedDate(selectedDate)}
                                </Typography>
                            </Box>

                            <IconButton
                                onClick={handleCloseDetails}
                                size="small"
                                aria-label="Close employee details"
                                sx={{
                                    border: "1px solid",
                                    borderColor: "divider",
                                    width: 34,
                                    height: 34,
                                }}
                            >
                                ×
                            </IconButton>
                        </Stack>
                    </Box>
                </DialogTitle>

                <DialogContent
                    dividers
                    sx={{
                        p: { xs: 1.5, sm: 2.25 },
                        backgroundColor: "background.default",
                    }}
                >
                    {detailsLoading && (
                        <Box
                            sx={{
                                minHeight: 300,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                            }}
                        >
                            <Stack alignItems="center" spacing={1.25}>
                                <CircularProgress size={30} />
                                <Typography variant="body2" color="text.secondary">
                                    Loading employee history...
                                </Typography>
                            </Stack>
                        </Box>
                    )}

                    {!detailsLoading && detailsError && (
                        <Alert
                            severity="error"
                            sx={{ borderRadius: 2.5 }}
                            onClose={() => setDetailsError("")}
                        >
                            {detailsError}
                        </Alert>
                    )}

                    {!detailsLoading && !detailsError && employeeDetails && (
                        <Stack spacing={2}>
                            {/* Selected day */}
                            <Card
                                elevation={0}
                                sx={{
                                    border: "1px solid",
                                    borderColor: "divider",
                                    borderRadius: 2.5,
                                }}
                            >
                                <CardContent
                                    sx={{
                                        p: { xs: 1.5, sm: 2 },
                                        "&:last-child": { pb: { xs: 1.5, sm: 2 } },
                                    }}
                                >
                                    <Stack
                                        direction="row"
                                        justifyContent="space-between"
                                        alignItems="center"
                                        spacing={1}
                                        sx={{ mb: 1.5 }}
                                    >
                                        <Box>
                                            <Typography variant="subtitle2" sx={{ fontWeight: 900 }}>
                                                Selected Day
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                Attendance summary for the selected date
                                            </Typography>
                                        </Box>

                                        <Chip
                                            size="small"
                                            variant="outlined"
                                            label={
                                                employeeDetails.organization ||
                                                employeeDetails.selectedDay?.organization ||
                                                "No organization"
                                            }
                                        />
                                    </Stack>

                                    <Box
                                        sx={{
                                            display: "grid",
                                            gridTemplateColumns: {
                                                xs: "repeat(2, 1fr)",
                                                sm: "repeat(4, 1fr)",
                                            },
                                            gap: 1,
                                        }}
                                    >
                                        {[
                                            ["In Time", employeeDetails.selectedDay?.checkIn || "-"],
                                            [
                                                "Out Time",
                                                employeeDetails.selectedDay?.checkOut || "-",
                                            ],
                                            [
                                                "Late by",
                                                formatMinutes(employeeDetails.selectedDay?.lateMinutes),
                                            ],
                                            [
                                                "Working hours",
                                                formatNullableNumber(
                                                    employeeDetails.selectedDay?.totalWorkingHours,
                                                ),
                                            ],
                                        ].map(([label, value]) => (
                                            <Paper
                                                key={label}
                                                elevation={0}
                                                sx={{
                                                    p: 1.25,
                                                    border: "1px solid",
                                                    borderColor: "divider",
                                                    borderRadius: 2,
                                                    backgroundColor: "background.paper",
                                                }}
                                            >
                                                <Typography
                                                    variant="caption"
                                                    color="text.secondary"
                                                    sx={{
                                                        display: "block",
                                                        mb: 0.35,
                                                    }}
                                                >
                                                    {label}
                                                </Typography>
                                                <Typography
                                                    variant="body2"
                                                    sx={{
                                                        fontWeight: 900,
                                                        whiteSpace: "nowrap",
                                                    }}
                                                >
                                                    {value}
                                                </Typography>
                                            </Paper>
                                        ))}
                                    </Box>

                                    <Stack
                                        direction="row"
                                        spacing={0.8}
                                        flexWrap="wrap"
                                        useFlexGap
                                        sx={{ mt: 1 }}
                                    >
                                        <Chip
                                            size="small"
                                            variant="outlined"
                                            label={`Hours: ${formatNullableNumber(
                                                employeeDetails.selectedDay?.hours,
                                            )}`}
                                        />
                                        <Chip
                                            size="small"
                                            variant="outlined"
                                            label={`Overtime: ${formatNullableNumber(
                                                employeeDetails.selectedDay?.overtime,
                                            )}`}
                                        />
                                    </Stack>
                                </CardContent>
                            </Card>

                            {/* Management indicators */}
                            <Box
                                sx={{
                                    display: "grid",
                                    gridTemplateColumns: {
                                        xs: "repeat(2, 1fr)",
                                        sm: "repeat(4, 1fr)",
                                    },
                                    gap: 1,
                                }}
                            >
                                {[
                                    [
                                        "Current streak",
                                        `${Number(employeeDetails.currentLateStreak || 0)} days`,
                                    ],
                                    [
                                        "Longest streak",
                                        `${Number(employeeDetails.longestLateStreak || 0)} days`,
                                    ],
                                    [
                                        "Current month rank",
                                        Number(employeeDetails.currentMonthRank || 0) || "-",
                                    ],
                                    [
                                        "Team avg",
                                        formatNullableNumber(
                                            employeeDetails.teamCurrentMonthAverageLateMinutes,
                                            " min",
                                        ),
                                    ],
                                ].map(([label, value]) => (
                                    <Paper
                                        key={label}
                                        elevation={0}
                                        sx={{
                                            p: 1.25,
                                            border: "1px solid",
                                            borderColor: "divider",
                                            borderRadius: 2.25,
                                            backgroundColor: "background.paper",
                                        }}
                                    >
                                        <Typography variant="caption" color="text.secondary">
                                            {label}
                                        </Typography>
                                        <Typography
                                            sx={{
                                                mt: 0.25,
                                                fontSize: 17,
                                                fontWeight: 900,
                                            }}
                                        >
                                            {value}
                                        </Typography>
                                    </Paper>
                                ))}
                            </Box>

                            {/* Frequency */}
                            <Box>
                                <Stack
                                    direction="row"
                                    justifyContent="space-between"
                                    alignItems="flex-end"
                                    sx={{ mb: 1.25 }}
                                >
                                    <Box>
                                        <Typography variant="subtitle2" sx={{ fontWeight: 900 }}>
                                            Late Arrival Pattern
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            Frequency and severity across historical periods
                                        </Typography>
                                    </Box>

                                    <Typography variant="caption" color="text.secondary">
                                        {Number(
                                            employeeDetails.currentMonthEmployeesWithLateData || 0,
                                        )}{" "}
                                        employees with late data
                                    </Typography>
                                </Stack>

                                <Box
                                    sx={{
                                        display: "grid",
                                        gridTemplateColumns: {
                                            xs: "1fr",
                                            sm: "repeat(2, 1fr)",
                                            md: "repeat(3, 1fr)",
                                        },
                                        gap: 1,
                                    }}
                                >
                                    <DetailPeriodCard
                                        title="Last 7 Days"
                                        period={employeeDetails.last7Days}
                                    />
                                    <DetailPeriodCard
                                        title="Last 14 Days"
                                        period={employeeDetails.last14Days}
                                    />
                                    <DetailPeriodCard
                                        title="Current Month"
                                        period={employeeDetails.currentMonth}
                                    />
                                    <DetailPeriodCard
                                        title="Previous Month"
                                        period={employeeDetails.previousMonth}
                                    />
                                    <DetailPeriodCard
                                        title="Last 3 Months"
                                        period={employeeDetails.last3Months}
                                    />
                                    <DetailPeriodCard
                                        title="Last 6 Months"
                                        period={employeeDetails.last6Months}
                                    />
                                </Box>
                            </Box>

                            {/* Personal analytics */}
                            <Box>
                                <Typography
                                    variant="subtitle2"
                                    sx={{
                                        fontWeight: 900,
                                        mb: 1.1,
                                    }}
                                >
                                    Personal Late-Arrival Analytics
                                </Typography>

                                <Box
                                    sx={{
                                        display: "grid",
                                        gridTemplateColumns: {
                                            xs: "1fr",
                                            md: "1.4fr 1fr",
                                        },
                                        gap: 1.25,
                                    }}
                                >
                                    <Card
                                        elevation={0}
                                        sx={{
                                            border: "1px solid",
                                            borderColor: "divider",
                                            borderRadius: 2.5,
                                        }}
                                    >
                                        <CardContent sx={{ p: 1.75 }}>
                                            <Typography
                                                variant="body2"
                                                sx={{
                                                    fontWeight: 850,
                                                }}
                                            >
                                                Late Minutes Trend
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                Available recorded late-arrival history
                                            </Typography>

                                            <Box sx={{ mt: 0.8 }}>
                                                <TrendChart
                                                    data={employeeCurrentMonthHistory || []}
                                                    series={[
                                                        {
                                                            key: "lateMinutes",
                                                            label: "Late minutes",
                                                            color: "#f36f43",
                                                            suffix: " min",
                                                        },
                                                    ]}
                                                    height={170}
                                                />
                                            </Box>
                                        </CardContent>
                                    </Card>

                                    <Card
                                        elevation={0}
                                        sx={{
                                            border: "1px solid",
                                            borderColor: "divider",
                                            borderRadius: 2.5,
                                        }}
                                    >
                                        <CardContent sx={{ p: 1.75 }}>
                                            <Typography
                                                variant="body2"
                                                sx={{
                                                    fontWeight: 850,
                                                }}
                                            >
                                                Current Month Severity
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                Severity mix for this employee
                                            </Typography>

                                            <SeverityDonut
                                                under15={
                                                    employeeDetails.currentMonth?.under15MinutesCount
                                                }
                                                moderate={
                                                    employeeDetails.currentMonth?.moderateLateCount
                                                }
                                                severe={employeeDetails.currentMonth?.severeLateCount}
                                                centerLabel="Late days"
                                                centerValue={Number(
                                                    employeeDetails.currentMonth?.lateDayCount || 0,
                                                )}
                                            />
                                        </CardContent>
                                    </Card>
                                </Box>

                                <Card
                                    elevation={0}
                                    sx={{
                                        mt: 1.25,
                                        border: "1px solid",
                                        borderColor: "divider",
                                        borderRadius: 2.5,
                                    }}
                                >
                                    <CardContent sx={{ p: 1.75 }}>
                                        <Typography
                                            variant="body2"
                                            sx={{
                                                fontWeight: 850,
                                            }}
                                        >
                                            Average Delay by Period
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            Short-term behaviour compared with longer-term history
                                        </Typography>

                                        <Box sx={{ mt: 1.3 }}>
                                            <PeriodComparisonChart
                                                periods={[
                                                    {
                                                        label: "Last 7 days",
                                                        value:
                                                            employeeDetails.last7Days?.averageLateMinutes,
                                                    },
                                                    {
                                                        label: "Last 14 days",
                                                        value:
                                                            employeeDetails.last14Days?.averageLateMinutes,
                                                    },
                                                    {
                                                        label: "Current month",
                                                        value:
                                                            employeeDetails.currentMonth?.averageLateMinutes,
                                                    },
                                                    {
                                                        label: "Previous month",
                                                        value:
                                                            employeeDetails.previousMonth?.averageLateMinutes,
                                                    },
                                                    {
                                                        label: "Last 3 months",
                                                        value:
                                                            employeeDetails.last3Months?.averageLateMinutes,
                                                    },
                                                    {
                                                        label: "Last 6 months",
                                                        value:
                                                            employeeDetails.last6Months?.averageLateMinutes,
                                                    },
                                                ]}
                                            />
                                        </Box>
                                    </CardContent>
                                </Card>
                            </Box>

                            {/* Selected month date-wise verification */}
                            <Card
                                elevation={0}
                                sx={{
                                    border: "1px solid",
                                    borderColor: "divider",
                                    borderRadius: 2.5,
                                }}
                            >
                                <CardContent sx={{ p: 0 }}>
                                    <Box sx={{ px: 2, py: 1.5 }}>
                                        <Stack
                                            direction={{ xs: "column", sm: "row" }}
                                            justifyContent="space-between"
                                            alignItems={{ xs: "flex-start", sm: "center" }}
                                            spacing={1}
                                        >
                                            <Box>
                                                <Typography
                                                    variant="subtitle2"
                                                    sx={{ fontWeight: 900 }}
                                                >
                                                    Current Month — Date-wise Late Arrivals
                                                </Typography>
                                                <Typography
                                                    variant="caption"
                                                    color="text.secondary"
                                                >
                                                    Every late occurrence in the selected month is listed below for verification.
                                                </Typography>
                                            </Box>

                                            <Stack
                                                direction="row"
                                                spacing={0.75}
                                                flexWrap="wrap"
                                                useFlexGap
                                            >
                                                <Chip
                                                    size="small"
                                                    color="error"
                                                    label={`Late records: ${Number(
                                                        employeeDetails.currentMonth?.lateRecordCount || 0,
                                                    )}`}
                                                    sx={{ fontWeight: 800 }}
                                                />
                                                <Chip
                                                    size="small"
                                                    variant="outlined"
                                                    label={`Late days: ${Number(
                                                        employeeDetails.currentMonth?.lateDayCount || 0,
                                                    )}`}
                                                    sx={{ fontWeight: 800 }}
                                                />
                                                <Chip
                                                    size="small"
                                                    variant="outlined"
                                                    label={`Dates shown: ${employeeCurrentMonthHistory.length}`}
                                                    sx={{ fontWeight: 800 }}
                                                />
                                            </Stack>
                                        </Stack>
                                    </Box>

                                    <Divider />

                                    {employeeCurrentMonthHistory.length ? (
                                        <TableContainer sx={{ maxHeight: 330 }}>
                                            <Table size="small" stickyHeader>
                                                <TableHead>
                                                    <TableRow>
                                                        <TableCell sx={{ fontWeight: 800, width: 55 }}>
                                                            #
                                                        </TableCell>
                                                        <TableCell sx={{ fontWeight: 800 }}>
                                                            Date
                                                        </TableCell>
                                                        <TableCell sx={{ fontWeight: 800 }}>
                                                            In Time
                                                        </TableCell>
                                                        <TableCell sx={{ fontWeight: 800 }}>
                                                            Late
                                                        </TableCell>
                                                        <TableCell sx={{ fontWeight: 800 }}>
                                                            Status
                                                        </TableCell>
                                                    </TableRow>
                                                </TableHead>

                                                <TableBody>
                                                    {employeeCurrentMonthHistory.map((item, index) => {
                                                        const meta = getStatusMeta(item?.lateMinutes);
                                                        const rawDate =
                                                            item?.date ??
                                                            item?.Date ??
                                                            item?.attendanceDate ??
                                                            item?.AttendanceDate;

                                                        return (
                                                            <TableRow
                                                                key={`month-detail-${rawDate}-${item?.checkIn}-${index}`}
                                                                hover
                                                            >
                                                                <TableCell>{index + 1}</TableCell>
                                                                <TableCell sx={{ fontWeight: 700 }}>
                                                                    {getRawDateOnly(rawDate)}
                                                                </TableCell>
                                                                <TableCell>
                                                                    {item?.checkIn || item?.CheckIn || "-"}
                                                                </TableCell>
                                                                <TableCell sx={{ fontWeight: 850 }}>
                                                                    {formatMinutes(
                                                                        item?.lateMinutes ??
                                                                        item?.LateMinutes ??
                                                                        0,
                                                                    )}
                                                                </TableCell>
                                                                <TableCell>
                                                                    <Chip
                                                                        size="small"
                                                                        label={
                                                                            item?.status ||
                                                                            item?.Status ||
                                                                            meta.label
                                                                        }
                                                                        color={meta.color}
                                                                        sx={{ fontWeight: 800 }}
                                                                    />
                                                                </TableCell>
                                                            </TableRow>
                                                        );
                                                    })}
                                                </TableBody>
                                            </Table>
                                        </TableContainer>
                                    ) : (
                                        <Box
                                            sx={{
                                                py: 3.5,
                                                px: 2,
                                                textAlign: "center",
                                            }}
                                        >
                                            <Typography variant="body2" color="text.secondary">
                                                No late-arrival records were found for this employee
                                                in the selected month.
                                            </Typography>
                                        </Box>
                                    )}
                                </CardContent>
                            </Card>

                        </Stack>
                    )}
                </DialogContent>

            </Dialog>
            <Dialog
                open={ftoDetailsOpen}
                onClose={handleCloseFtoDetails}
                fullWidth
                maxWidth="md"
            >
                <DialogTitle sx={{ fontWeight: 900 }}>
                    {selectedFtoEmployee?.employeeName || "FTO Details"}
                    {selectedFtoEmployee && (
                        <Typography variant="caption" color="text.secondary" display="block">
                            Employee ID: {selectedFtoEmployee?.employeeId || selectedFtoEmployee?.userId || "-"}
                        </Typography>
                    )}
                </DialogTitle>

                <DialogContent dividers>
                    {selectedFtoEmployee && (
                        <>
                            <Box
                                sx={{
                                    display: "grid",
                                    gridTemplateColumns: { xs: "1fr", sm: "repeat(3, 1fr)" },
                                    gap: 1,
                                    mb: 2,
                                }}
                            >
                                {[
                                    ["EL Balance", selectedFtoEmployee?.elBalance],
                                    ["CL Balance", selectedFtoEmployee?.clBalance],
                                    ["SL Balance", selectedFtoEmployee?.slBalance],
                                ].map(([label, value]) => (
                                    <Paper
                                        key={label}
                                        elevation={0}
                                        sx={{
                                            p: 1.4,
                                            border: "1px solid",
                                            borderColor: "divider",
                                            borderRadius: 2,
                                        }}
                                    >
                                        <Typography variant="caption" color="text.secondary">
                                            {label}
                                        </Typography>
                                        <Typography sx={{ fontSize: 20, fontWeight: 900 }}>
                                            {Number(value ?? 0).toFixed(1)}
                                        </Typography>
                                    </Paper>
                                ))}
                            </Box>

                            <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mb: 1.25 }}>
                                <Chip
                                    size="small"
                                    color="primary"
                                    label={`FTO Count: ${Number(selectedFtoEmployee?.ftoCount || 0)}`}
                                    sx={{ fontWeight: 800 }}
                                />
                                <Chip
                                    size="small"
                                    variant="outlined"
                                    label={`FTO Days: ${Number(selectedFtoEmployee?.totalFtoDays || 0).toFixed(2)}`}
                                    sx={{ fontWeight: 800 }}
                                />
                            </Stack>

                            <Typography variant="subtitle2" sx={{ fontWeight: 900, mb: 0.8 }}>
                                Current Month FTO Records
                            </Typography>

                            <TableContainer sx={{ maxHeight: 360 }}>
                                <Table size="small" stickyHeader>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell sx={{ fontWeight: 800 }}>#</TableCell>
                                            <TableCell sx={{ fontWeight: 800 }}>Start Date</TableCell>
                                            <TableCell sx={{ fontWeight: 800 }}>End Date</TableCell>
                                            <TableCell sx={{ fontWeight: 800 }}>Days</TableCell>
                                            <TableCell sx={{ fontWeight: 800 }}>Leave Type</TableCell>
                                            <TableCell sx={{ fontWeight: 800 }}>FTO Type</TableCell>
                                            <TableCell sx={{ fontWeight: 800 }}>Approval</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {(ftoOverview?.records || [])
                                            .filter(
                                                (record) =>
                                                    String(record?.userId ?? "") ===
                                                    String(selectedFtoEmployee?.userId ?? ""),
                                            )
                                            .map((record, index) => (
                                                <TableRow key={`fto-detail-${record?.id}-${index}`} hover>
                                                    <TableCell>{index + 1}</TableCell>
                                                    <TableCell>{getRawDateOnly(record?.startDate)}</TableCell>
                                                    <TableCell>{getRawDateOnly(record?.endDate)}</TableCell>
                                                    <TableCell sx={{ fontWeight: 800 }}>
                                                        {Number(record?.numberOfDays || 0).toFixed(2)}
                                                    </TableCell>
                                                    <TableCell>{getFtoLeaveTypeName(record)}</TableCell>
                                                    <TableCell>{getFtoTypeName(record)}</TableCell>
                                                    <TableCell>
                                                        <Chip
                                                            size="small"
                                                            label={record?.approvalStatus || "-"}
                                                            color={
                                                                [
                                                                    "Approved",
                                                                    "Self Approved",
                                                                    "Approved and Released",
                                                                ].includes(record?.approvalStatus)
                                                                    ? "success"
                                                                    : "warning"
                                                            }
                                                            sx={{ fontWeight: 800 }}
                                                        />
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </>
                    )}
                </DialogContent>
            </Dialog>
        </Box>
    );
}
